import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  SubModuleComponent,
  ValidationRule,
  ComponentType,
  InputType,
} from "../../services/types/ComponentTypes";
import { TextField } from "./TextField";
import { SelectDropdown } from "./SelectDropdown";
import { SearchableDropdown } from "./SearchableDropdown";
import { DateField } from "./DateField";
import { useAppSelector, useAppDispatch } from "../../state/hooks";
import {
  fetchLookup,
  fetchDependentLookup,
  fetchSubModuleRawData,
  clearLookup,
} from "../../state/slices/lookupsSlice";
import { LookupOption } from "../../services/api";
import { apiService } from "../../services/api";
import { getParentIdFromFormData } from "../../state/utils/configUtils";
import { showConfirmDialog } from "../../state/utils/confirmationDialogUtils";
import { getSubmoduleShowEditDelete } from "../../services/userPreferences";
import { runConditionalValidations } from "../../state/utils/conditionalValidationUtils";
import {
  openPDFViewer,
  setPDFViewerLoading,
  setPDFViewerError,
} from "../../state/slices/pdfViewerSlice";
import {
  openHTMLDrawer,
  setHTMLDrawerLoading,
  setHTMLDrawerError,
} from "../../state/slices/htmlDrawerSlice";
import { authService } from "../../services/auth";

interface SubModuleProps {
  config: SubModuleComponent;
  value?: Record<string, any>[];
  onChange?: (name: string, value: Record<string, any>[]) => void;
  formData?: Record<string, any>; // Add formData prop to access parent field values
  onFormDataChange?: (name: string, value: any) => void; // Add callback to update parent form data
  onRefreshAllIndependentLookups?: () => void; // Callback to refresh all independent lookups on the screen
  externalError?: string;
}


interface AutoResizeTextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> { }

const AutoResizeTextArea: React.FC<AutoResizeTextAreaProps> = (props) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      // 1. Reset height to 'auto' to correctly calculate scrollHeight (shrink if needed)
      textarea.style.height = "auto";

      // 2. Calculate new height: max(scrollHeight + buffer, minHeight of 100px)
      const newHeight = Math.max(textarea.scrollHeight + 2, 80);

      // 3. Set height with !important to override any other styles
      textarea.style.setProperty("height", `${newHeight}px`, "important");
    }
  };

  // Adjust on mount and value change
  React.useLayoutEffect(() => {
    adjustHeight();
  }, [props.value]);

  // Adjust on window resize as well, since width changes affect height
  useEffect(() => {
    window.addEventListener("resize", adjustHeight);
    return () => window.removeEventListener("resize", adjustHeight);
  }, []);

  return (
    <textarea
      ref={textareaRef}
      {...props}
      rows={1} // Force rows to 1 so it starts small and grows via JS
      onChange={(e) => {
        props.onChange?.(e);
        adjustHeight();
      }}
      style={{
        ...props.style,
        overflow: "hidden", // Hide scrollbar to avoid flickering
        resize: "none",
        height: "auto", // Initial style, will be overridden by JS
      }}
    />
  );
};

export const SubModule: React.FC<SubModuleProps> = ({
  config,
  value = [],
  onChange,
  formData = {},
  onFormDataChange,
  onRefreshAllIndependentLookups,
  externalError,
}) => {
  const [error, setError] = useState<string>("");
  // Initialize data with value prop - this ensures edit mode data is loaded immediately
  const [data, setData] = useState<Record<string, any>[]>(() => {
    // If value prop has data, use it (for edit mode)
    // Otherwise start with empty array
    return value && Array.isArray(value) && value.length > 0 ? value : [];
  });
  // Track if table should be shown (when user clicks "Add New" or when data exists)
  const [showTable, setShowTable] = useState<boolean>(() => {
    // Show table if there's already data (edit mode)
    return value && Array.isArray(value) && value.length > 0;
  });
  // Search functionality
  const [searchTerm, setSearchTerm] = useState<string>("");
  // Edit functionality - track which row is being edited
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
  const lookupsState = useAppSelector((state) => state.lookups);
  const dispatch = useAppDispatch();
  const previousParentValueRef = useRef<any>(undefined);
  const lastPopulatedDataRef = useRef<string>("");
  const isInitialMountRef = useRef(true);
  const lastLookupDataRef = useRef<any[]>([]);
  const lastValueRef = useRef<string>("");
  const onChangeRef = useRef(onChange);
  const isUpdatingFromInternalRef = useRef(false);
  const previousFooterValuesRef = useRef<Record<string, number>>({});
  const onFormDataChangeRef = useRef(onFormDataChange);
  const requestedLookupsRef = useRef<Set<string>>(new Set());
  const previousChildControlsRef = useRef<string>("");
  const lastProcessedParentValueRef = useRef<string | undefined>(undefined);
  const processedRawDataKeysRef = useRef<Set<string>>(new Set()); // Track which rawData keys we've already processed for ExpressionV2

  // Get global preference for showing Edit/Delete actions
  const globalShowEditDelete = getSubmoduleShowEditDelete();

  // Determine if Edit/Delete should be shown:
  // - Check config flags first (enableEditAction, enableDeleteAction)
  // - If not set in config, fall back to global preference
  // - Default to true if nothing is set
  const showEditAction = config.enableEditAction !== undefined
    ? config.enableEditAction
    : globalShowEditDelete;
  const showDeleteAction = config.enableDeleteAction !== undefined
    ? config.enableDeleteAction
    : globalShowEditDelete;
  const showViewAction = config.viewAction !== undefined;
  const showActionsColumn = showEditAction || showDeleteAction || showViewAction;

  // Keep onChange ref up to date
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Keep onFormDataChange ref up to date
  useEffect(() => {
    onFormDataChangeRef.current = onFormDataChange;
  }, [onFormDataChange]);

  // Helper function to extract property from item with multiple variations
  // CRITICAL: Checks for !== undefined && !== null to handle 0 values correctly
  const extractPropertyValue = (item: any, property: string): any => {
    if (!item || !property) return null;

    const variations = [
      property,
      property.toLowerCase(),
      property.toUpperCase(),
      property.replace(/_/g, ""),
      property.replace(/_/g, "").toLowerCase(),
      property.replace(/_([a-z])/g, (g: string) => g[1].toUpperCase())
    ];

    for (const variation of variations) {
      if (item[variation] !== undefined && item[variation] !== null) {
        return item[variation];
      }
    }

    return null;
  };

  // Helper function to resolve field references (shared between updateRowField and evaluateAllExpressionsForRow)
  const resolveFieldReference = (
    reference: string,
    rowData: Record<string, any>
  ): string | null => {
    const refValue = reference.replace(/[\[\]]/g, "");

    // Handle property access: [field.property]
    if (refValue.includes(".")) {
      const [fieldName, property] = refValue.split(".");
      let fieldValue = rowData[fieldName];

      // CRITICAL FIX: If fieldName not found in rowData, try Json_Attribute
      if (fieldValue === undefined || fieldValue === null) {
        const refControl = config.childControls?.find(
          (c: any) => (c.name || c.BindValue) === fieldName
        );
        if (refControl && (refControl as any).Json_Attribute) {
          fieldValue = rowData[(refControl as any).Json_Attribute];
        }
      }

      // First, check if fieldValue is already an object with the property
      if (fieldValue && typeof fieldValue === "object") {
        const propertyValue =
          fieldValue[property] ||
          fieldValue[property.toLowerCase()] ||
          fieldValue[property.toUpperCase()];
        if (propertyValue !== undefined && propertyValue !== null) {
          return String(propertyValue);
        }
      }

      // CRITICAL: If fieldValue is an ID (string/number), look it up in rawData
      // This handles cases like [drp_code.sale_rate] where drp_code is just an ID
      // and we need to find the code in the lookup data to get sale_rate
      if (fieldValue !== undefined && fieldValue !== null && fieldValue !== "") {
        // Find the control to get lookup information
        const refControl = config.childControls?.find(
          (c: any) => (c.name || c.BindValue) === fieldName
        );

        if (refControl) {
          const refControlAny = refControl as any;
          const lookupName =
            refControlAny?.LookupName || refControlAny?.lookupName;

          if (lookupName) {
            // Determine lookup key (handle dependent lookups)
            const parentFieldForLookup =
              refControlAny?.Parent_Field ||
              refControlAny?.parentField ||
              config.parentField;
            let lookupKey = lookupName;

            if (parentFieldForLookup) {
              const parentValue = formData[parentFieldForLookup];
              if (
                parentValue !== undefined &&
                parentValue !== null &&
                parentValue !== ""
              ) {
                lookupKey = `${lookupName}_${parentValue}`;
              }
            }

            // CRITICAL: Try rawData FIRST (has all properties like sale_rate, purchase_rate, etc.)
            const rawData = lookupsState.rawData[lookupKey] || [];
            const lookupOptions = lookupsState.lookups[lookupKey] || [];



            // First try to find in rawData (has all properties)
            // CRITICAL: Also match by label/name/description since Default_Value might set the label, not ID
            let rawItem = rawData.find(
              (item: any) =>
                item.id === fieldValue ||
                String(item.id) === String(fieldValue) ||
                item.value === fieldValue ||
                String(item.value) === String(fieldValue) ||
                (item.product_id &&
                  String(item.product_id) === String(fieldValue)) ||
                (item.codeid && String(item.codeid) === String(fieldValue)) ||
                // Match by label/name (for cases like "5%" being the label)
                item.label === fieldValue ||
                String(item.label) === String(fieldValue) ||
                item.name === fieldValue ||
                String(item.name) === String(fieldValue) ||
                item.description === fieldValue ||
                String(item.description) === String(fieldValue) ||
                // Match by tax_name or similar for tax_rates lookup
                item.tax_name === fieldValue ||
                String(item.tax_name) === String(fieldValue)
            );

            // If not found in rawData, try lookup options
            const selectedOption = rawItem
              ? null
              : lookupOptions.find(
                (opt: any) =>
                  opt.value === fieldValue ||
                  String(opt.value) === String(fieldValue) ||
                  opt.id === fieldValue ||
                  String(opt.id) === String(fieldValue) ||
                  // Match by label (for cases like "5%" being the label)
                  opt.label === fieldValue ||
                  String(opt.label) === String(fieldValue)
              );

            // Try to get property value - prefer rawItem, fallback to selectedOption
            let propValue = extractPropertyValue(rawItem, property);
            if (propValue === null && selectedOption) {
              propValue = extractPropertyValue(selectedOption, property);
            }

            if (
              propValue !== null &&
              propValue !== undefined &&
              propValue !== ""
            ) {

              return String(propValue);
            } else {
            }
          }
        }
      }

      // CRITICAL: If property lookup failed, check if the field already has a value in rowData
      // This prevents overwriting correct values that were loaded from backend
      // Only return "0" if the field doesn't already have a value
      const controlWithExpression = config.childControls?.find(
        (c: any) => {
          const expr = (c as any).ExpressionV2;
          return expr && typeof expr === "string" && expr.includes(`[${fieldName}.${property}]`);
        }
      );

      if (controlWithExpression) {
        const expressionFieldName = (controlWithExpression as any).name || (controlWithExpression as any).BindValue;
        const expressionJsonAttr = (controlWithExpression as any).Json_Attribute;

        // Check if the field already has a value (from backend)
        const existingValue = rowData[expressionFieldName] || rowData[expressionJsonAttr];
        if (existingValue !== undefined && existingValue !== null && existingValue !== "" && existingValue !== "0" && existingValue !== 0) {
          // Field already has a value, preserve it instead of overwriting with "0"

          return null; // Return null to preserve existing value
        }
      }

      // If property lookup failed and no existing value, return "0" for numeric calculations
      return "0";
    }

    // Simple field reference: [field]
    // Handle 0, "0", empty string, and undefined/null
    let fieldValue = rowData[refValue];

    // CRITICAL FIX: If refValue not found in rowData, try Json_Attribute
    if (fieldValue === undefined || fieldValue === null) {
      const refControl = config.childControls?.find(
        (c: any) => (c.name || c.BindValue) === refValue
      );
      if (refControl && (refControl as any).Json_Attribute) {
        fieldValue = rowData[(refControl as any).Json_Attribute];
      }
    }

    if (fieldValue !== undefined && fieldValue !== null) {
      // Convert to string, but preserve 0 as "0" for calculations
      if (fieldValue === 0 || fieldValue === "0") {
        return "0";
      }
      if (fieldValue === "" || String(fieldValue).trim() === "") {
        return "0"; // Treat empty as 0 for numeric calculations
      }
      return String(fieldValue);
    }

    // If field doesn't exist, return "0" for numeric calculations
    return "0";
  };

  // Helper function to evaluate all ExpressionV2 fields for a row
  const evaluateAllExpressionsForRow = (
    rowData: Record<string, any>,
    rowIndex: number
  ): Record<string, any> => {
    if (!config.childControls) return rowData;

    let updatedRowData = { ...rowData };

    // Evaluate all ExpressionV2 fields
    for (const childControl of config.childControls) {
      const childControlAny = childControl as any;
      const expressionV2 = childControlAny.ExpressionV2;

      if (expressionV2 && typeof expressionV2 === "string") {
        const childFieldName =
          childControlAny.name || childControlAny.BindValue;

        // Pattern Matching - Find all field references
        const fieldRefRegex = /\[[\w.()]*\]/g;
        const matches = expressionV2.match(fieldRefRegex) || [];

        if (matches.length === 0) continue;

        // Value Resolution - Replace all references with actual values
        let expression = expressionV2;
        let canEvaluate = true;

        for (const match of matches) {
          const resolvedValue = resolveFieldReference(match, updatedRowData);

          if (resolvedValue !== null) {
            expression = expression.replace(match, resolvedValue);
          } else {
            // CRITICAL: If resolveFieldReference returns null, it might mean:
            // 1. The lookup failed but we want to preserve existing value
            // 2. The field doesn't exist and we should use "0"
            // Check if this is a property lookup that should preserve existing value
            const refValue = match.replace(/[\[\]]/g, "");
            if (refValue.includes(".")) {
              const [fieldName, property] = refValue.split(".");
              const controlWithExpression = config.childControls?.find(
                (c: any) => {
                  const expr = (c as any).ExpressionV2;
                  return expr && typeof expr === "string" && expr.includes(`[${fieldName}.${property}]`);
                }
              );

              if (controlWithExpression) {
                const expressionFieldName = (controlWithExpression as any).name || (controlWithExpression as any).BindValue;
                const expressionJsonAttr = (controlWithExpression as any).Json_Attribute;
                const existingValue = updatedRowData[expressionFieldName] || updatedRowData[expressionJsonAttr];

                // If field has existing value, preserve it by skipping this expression evaluation
                if (existingValue !== undefined && existingValue !== null && existingValue !== "" && existingValue !== "0" && existingValue !== 0) {

                  canEvaluate = false; // Skip evaluation to preserve existing value
                  break; // Exit the match loop
                }
              }
            }

            // If we should continue, use "0" as fallback
            if (canEvaluate) {
              expression = expression.replace(match, "0");
            }
          }
        }

        // Expression Evaluation - Only if all references resolved
        if (canEvaluate) {
          try {
            const evaluatedValue = Function(
              `"use strict"; return (${expression})`
            )();

            if (evaluatedValue !== null && evaluatedValue !== undefined) {
              const resultValue =
                evaluatedValue === "" ? "" : String(evaluatedValue);
              // Don't overwrite editable fields that already have a value (user may have changed it)
              const isReadOnly =
                childControlAny.disabled || childControlAny.IsReadOnly;
              const currentValue =
                updatedRowData[childFieldName] ??
                updatedRowData[childControlAny.Json_Attribute];
              const hasUserValue =
                currentValue !== undefined &&
                currentValue !== null &&
                currentValue !== "" &&
                currentValue !== "0" &&
                Number(currentValue) !== 0;
              if (!isReadOnly && hasUserValue) {
                // Preserve user-edited value (e.g. Rate on purchase screen)
                continue;
              }
              updatedRowData[childFieldName] = resultValue;
              if (childControlAny.Json_Attribute) {
                updatedRowData[childControlAny.Json_Attribute] = resultValue;
              }
            }
          } catch (error) {

          }
        }
      }
    }

    return updatedRowData;
  };

  // Sync value prop changes to internal state
  useEffect(() => {
    // Only sync if value prop actually changed and is different from current data
    const valueString = JSON.stringify(value || []);

    // CRITICAL FIX: Prevent data loss during screen resize
    // If we have existing data in local state, don't reset it if value prop becomes temporarily empty
    // This prevents data loss when components re-render during screen size changes
    const hasExistingData = data && Array.isArray(data) && data.length > 0;
    const hasValueData = value && Array.isArray(value) && value.length > 0;

    // Always sync value prop to internal state if it has data (for edit mode)
    // This ensures edit mode data is loaded properly
    if (valueString !== lastValueRef.current || (hasValueData && data.length === 0)) {
      const dataString = JSON.stringify(data);

      if (valueString !== dataString) {
        // If value prop has data, always use it (e.g., in edit mode)
        // CRITICAL FIX: Only sync if value is non-empty OR if both are empty.
        // This prevents transitional empty strings/arrays from parent from wiping out
        // valid internal data during screen initialization/edit mode loading or screen resize.
        // IMPORTANT: Don't reset data if we have existing data and value prop is empty (prevents data loss on resize)
        if (hasValueData) {
          // CRITICAL: Evaluate ExpressionV2 for all rows when data is loaded from value prop
          // This ensures calculated fields (like amount, vat_amt, net_amt) are computed
          // even if backend didn't return them or they need recalculation
          const rowsWithExpressions = value.map((row: any, index: number) => {
            return evaluateAllExpressionsForRow(row, index);
          });

          // CRITICAL: Proactively fetch rawData for lookups referenced in ExpressionV2 property lookups
          // This ensures rate fields (e.g., [drp_code.sale_rate]) can be calculated when rawData loads
          // This is a generic solution that works for all SubModules
          if (config.childControls) {
            for (const control of config.childControls) {
              const controlAny = control as any;
              const expression = controlAny.ExpressionV2;
              if (!expression || typeof expression !== "string") continue;

              // Check if expression contains property access (e.g., [field.property])
              if (/\[[\w]+\.[\w]+\]/.test(expression)) {
                // Extract field references with properties (e.g., [drp_code.sale_rate])
                const propertyRefRegex = /\[([\w]+)\.([\w]+)\]/g;
                const matches = expression.matchAll(propertyRefRegex);

                for (const match of matches) {
                  const fieldName = match[1]; // e.g., "drp_code"

                  // Find the control that this field references
                  const refControl = config.childControls?.find(
                    (c: any) => (c.name || c.BindValue) === fieldName
                  );

                  if (!refControl) continue;

                  const lookupName = (refControl as any).LookupName || (refControl as any).lookupName;
                  if (!lookupName) continue;

                  // Determine lookup key
                  const parentFieldForLookup =
                    (refControl as any).Parent_Field ||
                    (refControl as any).parentField ||
                    config.parentField;
                  let lookupKey = lookupName;

                  if (parentFieldForLookup) {
                    const parentValue = formData[parentFieldForLookup];
                    if (
                      parentValue !== undefined &&
                      parentValue !== null &&
                      parentValue !== ""
                    ) {
                      lookupKey = `${lookupName}_${parentValue}`;
                    }
                  }

                  // Check if rawData is already loaded
                  // Check if rawData is already loaded
                  const rawData = lookupsState.rawData[lookupKey] || [];
                  const isLoading = lookupsState.loading[lookupKey] || false;
                  const requestKey = `rawdata_proactive_${lookupKey}`;
                  const lastFetched = lookupsState.lastFetched[lookupKey];
                  // Fetch rawData if not loaded and not already requested
                  if (rawData.length === 0 && !isLoading && !lastFetched && !requestedLookupsRef.current.has(requestKey)) {

                    requestedLookupsRef.current.add(requestKey);
                    dispatch(
                      fetchSubModuleRawData({
                        lookupName,
                        parentValue: parentFieldForLookup ? (formData[parentFieldForLookup] || "") : "",
                        lookuptype: (refControl as any).componentType === ComponentType.SEARCHABLE_DROPDOWN ? "Select" : "Select",
                      })
                    );
                  }
                }
              }
            }
          }

          isUpdatingFromInternalRef.current = true;
          setData(rowsWithExpressions);
          // Show table if data exists
          if (rowsWithExpressions.length > 0) {
            setShowTable(true);
          }
          const validationError = validateData(rowsWithExpressions);
          setError(validationError);
          lastValueRef.current = valueString;
          // Mark that we have existing data from value prop to prevent auto-population
          lastPopulatedDataRef.current = valueString;

          // CRITICAL: When edit mode data is loaded, mark data as loaded to trigger footer calculation
          // This ensures footer amounts are calculated when data is loaded from value prop
          // IMPORTANT: Set dataLoadedRef immediately so footer calculation knows data is ready
          dataLoadedRef.current = true;


          // Note: The useEffect that watches data/value changes will also set dataLoadedRef,
          // but setting it here ensures footer calculation is triggered immediately
        } else if (!hasValueData) {
          // CRITICAL FIX: If value prop is empty, preserve existing data to prevent loss during resize
          // Only update ref if we don't have existing data (both are empty)
          // This ensures data is not lost when formData temporarily becomes empty during re-render
          if (!hasExistingData) {
            // Both are empty, just update ref
            lastValueRef.current = valueString;
          } else {
            // We have existing data but value prop is empty - preserve existing data
            // This prevents data loss during screen resize when formData might temporarily be empty

            // Don't update lastValueRef to prevent sync loop, but also don't reset data
            // The data will be preserved in local state
          }
        }

        if (isInitialMountRef.current) {
          isInitialMountRef.current = false;
        }
      } else if (isInitialMountRef.current) {
        // On initial mount, just initialize refs
        lastValueRef.current = valueString;
        isInitialMountRef.current = false;
      }
    }
  }, [value, data, config.name]);

  const validateData = (dataToValidate: Record<string, any>[]): string => {
    const activeDataToValidate = dataToValidate.filter(r => r.soft_delete !== 1 && r.soft_delete !== "1");

    // Check for NotAllowEmpty
    if (config.NotAllowEmpty && activeDataToValidate.length === 0) {
      return config.errorText || "At least one entry is required";
    }

    if (config.validation) {
      for (const rule of config.validation) {
        switch (rule.rule) {
          case ValidationRule.REQUIRED:
            if (activeDataToValidate.length === 0)
              return rule.message || "At least one entry is required";
            break;
          case ValidationRule.MIN_VALUE:
            if (activeDataToValidate.length < (rule.value as number))
              return rule.message || `Minimum ${rule.value} entries required`;
            break;
          case ValidationRule.MAX_VALUE:
            if (activeDataToValidate.length > (rule.value as number))
              return rule.message || `Maximum ${rule.value} entries allowed`;
            break;
        }
      }
    }
    // Run expression-based conditional validations (e.g. [qty] >= [issued_qty]) per row
    const conditionalResults = runConditionalValidations(activeDataToValidate, config.childControls);
    const blockingError = conditionalResults.find((r) => !r.isWarning);
    if (blockingError) return `Row ${blockingError.rowIndex}: ${blockingError.message}`;
    return "";
  };

  const handleDataChange = (newData: Record<string, any>[]) => {
    isUpdatingFromInternalRef.current = true;
    setData(newData);
    const validationError = validateData(newData);
    setError(validationError);
    lastValueRef.current = JSON.stringify(newData);
    // Use name first (matches ComponentRenderer's priority: name || BindValue)
    // ComponentRenderer normalizes config.name, so we should use that
    const fieldName = config.name || (config as any).BindValue;

    // CRITICAL FIX: Log the data change for debugging


    onChangeRef.current?.(fieldName, newData);
  };



  const addRow = () => {
    const activeRowsCount = data.filter((row) => row.soft_delete !== 1 && row.soft_delete !== "1").length;
    if (config.maxRows && activeRowsCount >= config.maxRows) return;

    // Show table when adding first row
    if (!showTable) {
      setShowTable(true);
    }

    const newRow: Record<string, any> = {};
    config.childControls?.forEach((control: any) => {
      // Handle both old format (BindValue) and new format (name)
      const fieldName = control.name || control.BindValue;
      if (fieldName) {
        let defaultValue = control.defaultValue || control.Default_Value || "";

        // CRITICAL FIX: If default value is provided for a lookup dropdown, ensure it's mapped to ID
        // This handles cases where defaultValue is "5%" (label) but we need the ID
        const isDropdown =
          control.componentType === ComponentType.SELECT_DROPDOWN ||
          control.componentType === ComponentType.SEARCHABLE_DROPDOWN ||
          control.componentType === "selectDropdown" ||
          control.componentType === "searchableDropdown";

        if (isDropdown && (control.lookupName || control.LookupName) && defaultValue) {
          const lookupName = control.lookupName || control.LookupName;

          // Determine lookup key using the same logic as other functions
          const parentFieldForLookup = control.Parent_Field || control.parentField || config.parentField;
          let lookupKey = lookupName;

          if (parentFieldForLookup) {
            const parentValue = formData[parentFieldForLookup];
            if (parentValue !== undefined && parentValue !== null && parentValue !== "") {
              lookupKey = `${lookupName}_${parentValue}`;
            }
          }

          // Check lookupsState for matching option
          const lookupOptions = lookupsState.lookups[lookupKey] || [];

          if (lookupOptions.length > 0) {
            // Try to find by Label, Name, or Value
            const matchingOption = lookupOptions.find((opt: any) =>
              opt.label === defaultValue ||
              String(opt.label) === String(defaultValue) ||
              (opt.name && String(opt.name) === String(defaultValue)) ||
              (opt.value && String(opt.value) === String(defaultValue)) ||
              (opt.id && String(opt.id) === String(defaultValue))
            );

            if (matchingOption) {
              // If found, use the ID/Value from the option
              // Prefer value/id over the label
              defaultValue = matchingOption.value || (matchingOption as any).id || defaultValue;
            }
          }
        }

        newRow[fieldName] = defaultValue;
      }
    });

    // Evaluate all ExpressionV2 fields for the new row
    const evaluatedRow = evaluateAllExpressionsForRow(newRow, data.length);



    // Clear any previous errors
    setError("");
    // Add new row at the beginning so it appears below the "Add New Row" button
    handleDataChange([evaluatedRow, ...data]);
  };

  const removeRow = async (index: number) => {
    const activeRowsCount = data.filter((r) => r.soft_delete !== 1 && r.soft_delete !== "1").length;
    if (config.minRows && activeRowsCount <= config.minRows) return;

    // Get the row data to show in confirmation message
    const row = data[index];
    const firstField = config.childControls?.[0] as any;
    const fieldName = firstField?.name || firstField?.BindValue || "";
    const jsonAttribute = firstField?.Json_Attribute || "";
    const rowLabel = (fieldName && row[fieldName]) || (jsonAttribute && row[jsonAttribute]) || "this item";

    // Show confirmation dialog
    const confirmed = await showConfirmDialog(
      dispatch,
      `Are you sure you want to delete ?`,
      "Confirm Delete"
    );

    if (!confirmed) {
      return; // User cancelled
    }

    let newData;
    if (row.id || row.itemid) {
      // Existing row - soft delete
      newData = [...data];
      newData[index] = { ...row, soft_delete: 1, act: "edit" };
    } else {
      // New row - hard delete
      newData = data.filter((_, i) => i !== index);
    }

    handleDataChange(newData);

    // Hide table if all active rows are removed
    if (newData.filter((r) => r.soft_delete !== 1 && r.soft_delete !== "1").length === 0) {
      setShowTable(false);
    }
  };

  const updateRowField = (
    rowIndex: number,
    fieldName: string,
    fieldValue: any,
    control?: any
  ) => {
    // CRITICAL FIX: Ensure row exists before updating
    const newData = [...data];

    // Ensure the row exists
    if (!newData[rowIndex]) {
      newData[rowIndex] = {};
    }

    // CRITICAL FIX: Store the full selected option if available
    // This matches the old framework's SelectedJson.Data approach
    // The selected option should contain ALL properties from rawData for ExpressionV2 to work
    const selectedOptionKey = (control as any)?._tempSelectedOptionKey;
    const selectedOption = (control as any)?._tempSelectedOption;

    // CRITICAL FIX: Ensure fieldValue is properly stored (handle null/undefined/empty string)
    const normalizedValue =
      fieldValue !== undefined && fieldValue !== null ? fieldValue : "";

    // Always store under the visible field name (name/BindValue)
    newData[rowIndex] = {
      ...newData[rowIndex],
      [fieldName]: normalizedValue,
      ...(newData[rowIndex].id || newData[rowIndex].itemid ? { act: "edit" } : {})
    };

    // CRITICAL: Also mirror the value under Json_Attribute (API field name) if present.
    // This ensures subform payload builder can always find latest values by Json_Attribute
    // even if expressions or auto-population originally used a different key.
    const jsonAttributeKey =
      (control as any)?.Json_Attribute || (control as any)?.jsonAttribute;
    if (jsonAttributeKey && typeof jsonAttributeKey === "string") {
      newData[rowIndex][jsonAttributeKey] = normalizedValue;
    }

    // CRITICAL FIX: Log the update for debugging
    if (import.meta.env.DEV) {
      console.log(`[SubModule] updateRowField - Storing value:`, {
        rowIndex,
        fieldName,
        fieldValue: normalizedValue,
        valueType: typeof normalizedValue,
        selectedOptionKey,
        hasSelectedOption: !!selectedOption,
        currentRowData: newData[rowIndex],
      });
    }

    // Store the full selected option in row data for ExpressionV2 evaluation
    // This allows expressions like [drp_product.sale_rate] to access properties
    if (selectedOptionKey && selectedOption) {
      newData[rowIndex][selectedOptionKey] = selectedOption;
      // Clear temp values
      delete (control as any)._tempSelectedOption;
      delete (control as any)._tempSelectedOptionKey;
    } else if (
      (control as any)?.componentType === ComponentType.SEARCHABLE_DROPDOWN ||
      (control as any)?.componentType === ComponentType.SELECT_DROPDOWN ||
      (control as any)?.componentType === "searchableDropdown" ||
      (control as any)?.componentType === "selectDropdown"
    ) {
      // If no selected option was provided, try to get it from rawData/lookups
      // This ensures ExpressionV2 can still access properties even if storage failed
      const controlAny = control as any;
      const lookupName = controlAny?.LookupName || controlAny?.lookupName;
      if (lookupName && fieldValue) {
        const parentFieldForLookup =
          controlAny?.Parent_Field ||
          controlAny?.parentField ||
          config.parentField;
        let lookupKey = lookupName;

        if (parentFieldForLookup) {
          const parentValue = formData[parentFieldForLookup];
          if (
            parentValue !== undefined &&
            parentValue !== null &&
            parentValue !== ""
          ) {
            lookupKey = `${lookupName}_${parentValue}`;
          }
        }

        // Try to get from rawData first (has all properties)
        const rawData = lookupsState.rawData[lookupKey] || [];
        const rawItem = rawData.find(
          (item: any) =>
            item.id === fieldValue ||
            String(item.id) === String(fieldValue) ||
            item.value === fieldValue ||
            String(item.value) === String(fieldValue) ||
            (item.product_id &&
              String(item.product_id) === String(fieldValue)) ||
            (item.codeid && String(item.codeid) === String(fieldValue))
        );

        if (rawItem) {
          const fallbackSelectedOptionKey = `_${fieldName}_selectedOption`;
          newData[rowIndex][fallbackSelectedOptionKey] = rawItem;
        } else {
          // CRITICAL: If rawData is empty, try to fetch it
          // This can happen if rawData wasn't fetched yet when user selects a value
          const isRawDataLoading = lookupsState.loading[lookupKey] || false;
          const lastFetched = lookupsState.lastFetched[lookupKey];
          const requestKey = `rawdata_updateRowField_${lookupKey}`;

          if (!isRawDataLoading && !lastFetched && rawData.length === 0 && !requestedLookupsRef.current.has(requestKey)) {
            requestedLookupsRef.current.add(requestKey);
            dispatch(
              fetchSubModuleRawData({
                lookupName,
                parentValue: parentFieldForLookup
                  ? formData[parentFieldForLookup] || ""
                  : "",
                lookuptype:
                  controlAny?.componentType ===
                    ComponentType.SEARCHABLE_DROPDOWN
                    ? "Select"
                    : "Select",
              })
            );
          }
        }
      }
    }

    // Handle BindParent auto-population
    // Check all childControls to see if any have BindParent pointing to this field
    if (fieldValue && config.childControls) {
      for (const childControl of config.childControls) {
        const childControlAny = childControl as any;
        const childBindParent =
          childControlAny.BindParent || childControlAny.bindParent;
        if (childBindParent && childBindParent[fieldName]) {
          // This child field should be populated from the selected lookup option
          const childFieldName =
            childControlAny.name || childControlAny.BindValue;
          const childFieldAttribute = childBindParent[fieldName]; // e.g., "item_name"

          // Get the lookup for the parent field (the one that was just changed)
          const controlAny = control as any;
          const lookupName = controlAny?.LookupName || controlAny?.lookupName;
          if (lookupName && childFieldName) {
            // Determine lookup key (handle dependent lookups)
            const parentFieldForLookup =
              controlAny?.Parent_Field ||
              controlAny?.parentField ||
              config.parentField;
            let lookupKey = lookupName;

            if (parentFieldForLookup) {
              const parentValue = formData[parentFieldForLookup];
              if (
                parentValue !== undefined &&
                parentValue !== null &&
                parentValue !== ""
              ) {
                lookupKey = `${lookupName}_${parentValue}`;
              }
            }

            // CRITICAL FIX: Use rawData first to access all properties (like item_name, purchase_rate, etc.)
            // rawData contains the full API response with all properties, while lookups only has label/value
            const rawData = lookupsState.rawData[lookupKey] || [];
            const lookupOptions = lookupsState.lookups[lookupKey] || [];

            // First try to find in rawData (has all properties)
            let rawItem = rawData.find(
              (item: any) =>
                item.id === fieldValue ||
                String(item.id) === String(fieldValue) ||
                item.value === fieldValue ||
                String(item.value) === String(fieldValue) ||
                (item.product_id &&
                  String(item.product_id) === String(fieldValue)) ||
                (item.codeid && String(item.codeid) === String(fieldValue)) ||
                // Match by label/name (for cases like "5%" being the label)
                item.label === fieldValue ||
                String(item.label) === String(fieldValue) ||
                item.name === fieldValue ||
                String(item.name) === String(fieldValue) ||
                item.description === fieldValue ||
                String(item.description) === String(fieldValue) ||
                // Match by tax_name or similar for tax_rates lookup
                item.tax_name === fieldValue ||
                String(item.tax_name) === String(fieldValue)
            );

            // If not found in rawData, try lookup options
            const selectedOption = rawItem
              ? null
              : lookupOptions.find(
                (opt: any) =>
                  opt.value === fieldValue ||
                  String(opt.value) === String(fieldValue) ||
                  opt.id === fieldValue ||
                  String(opt.id) === String(fieldValue)
              );

            // Get the attribute value - prefer rawData, fallback to lookup option
            let attributeValue = extractPropertyValue(rawItem, childFieldAttribute);
            if (attributeValue === null && selectedOption) {
              attributeValue = extractPropertyValue(selectedOption, childFieldAttribute) || (selectedOption.label || "");
            }
            attributeValue = attributeValue !== null ? String(attributeValue) : "";

            if (attributeValue) {
              newData[rowIndex] = {
                ...newData[rowIndex],
                [childFieldName]: attributeValue,
              };

              // CRITICAL FIX: Trigger ExpressionV2 evaluation after BindParent updates
              // This ensures calculated fields (like rate, amount, VAT) update when BindParent fields change
              // We mark that this field was updated by BindParent so ExpressionV2 knows to evaluate
              (childControlAny as any)._bindParentUpdated = true;
            }
          }
        }
      }
    }

    // CRITICAL FIX: After BindParent updates, trigger ExpressionV2 evaluation for fields that depend on BindParent-populated fields
    // This ensures cascading calculations work correctly (e.g., item_name → rate → amount → VAT → net)
    if (config.childControls) {
      const bindParentUpdatedFields = config.childControls
        .filter((c: any) => (c as any)._bindParentUpdated)
        .map((c: any) => {
          const fieldName = c.name || c.BindValue;
          delete (c as any)._bindParentUpdated; // Clean up flag
          return fieldName;
        });

      // Trigger ExpressionV2 evaluation for all fields that might depend on BindParent-populated fields
      // This will be handled by the ExpressionV2 evaluation logic below
    }

    // Helper function to resolve field references (like old ResolveFieldReference)
    const resolveFieldReference = (
      reference: string,
      rowData: Record<string, any>
    ): string | null => {
      // Remove brackets: [field] -> field
      let refValue = reference.replace(/[\[\]]/g, "");

      // Check for aggregate functions: [SUM:field]
      if (refValue.includes(":")) {
        // Aggregate functions are handled separately in FooterControls
        return null;
      }

      // Check for cross-form references: [(form).field] (future implementation)
      if (refValue.includes("(")) {
        // Extract form name and field - future implementation
        return null;
      }

      // Check for dot notation: [field.property]
      if (refValue.includes(".")) {
        const [fieldName, property] = refValue.split(".");
        const fieldValue = rowData[fieldName];

        if (!fieldValue) {
          return null;
        }

        // CRITICAL FIX: First check if the selected option is stored in rowData
        // This matches the old framework's SelectedJson.Data approach
        // The selected option should contain ALL properties from rawData
        const selectedOptionKey = `_${fieldName}_selectedOption`;
        const storedSelectedOption = rowData[selectedOptionKey];

        if (storedSelectedOption) {
          // Try to get property from stored selected option (like old framework's selectedProduct.Data[property])
          let propValue = extractPropertyValue(storedSelectedOption, property);

          if (
            propValue !== null &&
            propValue !== undefined &&
            propValue !== ""
          ) {
            return String(propValue);
          }
          // Don't return null yet - fall through to rawData lookup
        }

        // Find the control to get lookup information
        const refControl = config.childControls?.find(
          (c: any) => (c.name || c.BindValue) === fieldName
        );

        if (refControl) {
          const refControlAny = refControl as any;
          const lookupName =
            refControlAny?.LookupName || refControlAny?.lookupName;

          if (lookupName) {
            // Determine lookup key (handle dependent lookups)
            const parentFieldForLookup =
              refControlAny?.Parent_Field ||
              refControlAny?.parentField ||
              config.parentField;
            let lookupKey = lookupName;

            if (parentFieldForLookup) {
              const parentValue = formData[parentFieldForLookup];
              if (
                parentValue !== undefined &&
                parentValue !== null &&
                parentValue !== ""
              ) {
                lookupKey = `${lookupName}_${parentValue}`;
              }
            }

            // CRITICAL FIX: Try rawData FIRST (has all properties), then fallback to lookup options
            const rawData = lookupsState.rawData[lookupKey] || [];
            const lookupOptions = lookupsState.lookups[lookupKey] || [];

            // First try to find in rawData (has all properties like purchase_rate, sale_rate, etc.)
            let rawItem = rawData.find(
              (item: any) =>
                item.id === fieldValue ||
                String(item.id) === String(fieldValue) ||
                item.value === fieldValue ||
                String(item.value) === String(fieldValue) ||
                (item.product_id &&
                  String(item.product_id) === String(fieldValue)) ||
                (item.codeid && String(item.codeid) === String(fieldValue)) ||
                // Match by label/name (for cases like "5%" being the label)
                item.label === fieldValue ||
                String(item.label) === String(fieldValue) ||
                item.name === fieldValue ||
                String(item.name) === String(fieldValue) ||
                item.description === fieldValue ||
                String(item.description) === String(fieldValue) ||
                // Match by tax_name or similar for tax_rates lookup
                item.tax_name === fieldValue ||
                String(item.tax_name) === String(fieldValue)
            );

            // If not found in rawData, try lookup options
            const selectedOption = rawItem
              ? null
              : lookupOptions.find(
                (opt: any) =>
                  opt.value === fieldValue ||
                  String(opt.value) === String(fieldValue) ||
                  opt.id === fieldValue ||
                  String(opt.id) === String(fieldValue)
              );

            // Try to get property value - prefer rawItem, fallback to selectedOption
            let propValue = extractPropertyValue(rawItem, property);
            if (propValue === null && selectedOption) {
              propValue = extractPropertyValue(selectedOption, property);
            }
            if (
              propValue !== null &&
              propValue !== undefined &&
              propValue !== ""
            ) {
              return String(propValue);
            }
          }
        }

        return null;
      }

      // Simple field reference: [field]
      if (rowData[refValue] !== undefined && rowData[refValue] !== null) {
        return String(rowData[refValue]);
      }
      // CRITICAL: In edit mode row data may be keyed by Json_Attribute (e.g. debit, credit) from API
      // while ExpressionV2 references BindValue (e.g. txt_debit, txt_credit). Resolve by control mapping.
      const refControl = config.childControls?.find(
        (c: any) => (c.name || c.BindValue) === refValue
      );
      if (refControl && (refControl as any).Json_Attribute) {
        const jsonAttr = (refControl as any).Json_Attribute;
        const val = rowData[jsonAttr];
        if (val !== undefined && val !== null) {
          return String(val);
        }
      }

      return null;
    };

    // Handle ExpressionV2 evaluation for childControls
    // Based on old framework: SetExpressions() method
    // Only evaluate expressions that depend on the changed field
    if (config.childControls) {
      for (const childControl of config.childControls) {
        const childControlAny = childControl as any;
        const expressionV2 = childControlAny.ExpressionV2;

        if (expressionV2 && typeof expressionV2 === "string") {
          const childFieldName =
            childControlAny.name || childControlAny.BindValue;

          // Step 2: Dependency Detection - Check if expression depends on the changed field
          // Also check if expression depends on fields that were updated by BindParent
          const dependsOnChangedField =
            expressionV2.includes(`[${fieldName}]`) ||
            expressionV2.includes(`[${fieldName}.`);

          // Check if this expression depends on any BindParent-updated fields
          const bindParentUpdatedFields =
            config.childControls
              ?.filter((c: any) => (c as any)._bindParentUpdated)
              .map((c: any) => c.name || c.BindValue) || [];
          const dependsOnBindParentField = bindParentUpdatedFields.some(
            (updatedField: string) =>
              expressionV2.includes(`[${updatedField}]`) ||
              expressionV2.includes(`[${updatedField}.`)
          );

          // Evaluate if expression depends on changed field OR BindParent-updated field
          if (!dependsOnChangedField && !dependsOnBindParentField) {
            continue; // Skip expressions that don't depend on the changed field or BindParent field
          }

          // Step 3: Pattern Matching - Find all field references
          // Regex matches: [field], [field.property], [(form).field]
          const fieldRefRegex = /\[[\w.()]*\]/g;
          const matches = expressionV2.match(fieldRefRegex) || [];

          if (matches.length === 0) continue;

          // Step 4: Value Resolution - Replace all references with actual values
          let expression = expressionV2;
          let canEvaluate = true;
          const resolvedValues: Record<string, string> = {};

          // CRITICAL: Use the current rowData which should have the stored selected option
          const currentRowData = newData[rowIndex];

          for (const match of matches) {
            const resolvedValue = resolveFieldReference(match, currentRowData);

            if (resolvedValue !== null) {
              resolvedValues[match] = resolvedValue;
              expression = expression.replace(match, resolvedValue);
            } else {
              // Can't evaluate if any reference is missing
              canEvaluate = false;
              break;
            }
          }

          // Step 5: Expression Evaluation - Only if all references resolved
          if (canEvaluate) {
            try {
              // Use JavaScript evaluation (like old framework's window.Evaluate)
              const evaluatedValue = Function(
                `"use strict"; return (${expression})`
              )();

              // Step 6: Result Update
              // Check if field is read-only or has existing value (like old framework)
              const isReadOnly =
                childControlAny.disabled || childControlAny.IsReadOnly;
              const currentValue = newData[rowIndex]?.[childFieldName];
              const hasExistingValue =
                currentValue !== undefined &&
                currentValue !== null &&
                currentValue !== "" &&
                Number(currentValue) !== 0;

              // Update if: read-only field OR no existing value OR force update
              // OR the changed field is the expression's source (e.g. Code changed -> update Rate from new product)
              const shouldUpdate =
                isReadOnly ||
                !hasExistingValue ||
                childControlAny.ForceExpressionUpdate ||
                dependsOnChangedField;

              if (
                shouldUpdate &&
                evaluatedValue !== null &&
                evaluatedValue !== undefined
              ) {
                const resultValue =
                  evaluatedValue === "" ? "" : String(evaluatedValue);

                newData[rowIndex] = {
                  ...newData[rowIndex],
                  [childFieldName]: resultValue,
                };
                if (childControlAny.Json_Attribute) {
                  newData[rowIndex][childControlAny.Json_Attribute] = resultValue;
                }

                // CRITICAL FIX: Recursively evaluate other fields that might depend on this one
                // This handles chained expressions like rate -> amount -> vat_amt -> net_amt
                // Use synchronous recursive evaluation with dependency tracking to prevent infinite loops
                const evaluatedFields = new Set<string>(); // Track fields already evaluated in this cycle
                evaluatedFields.add(childFieldName);

                const evaluateDependentFields = (
                  fieldNameToCheck: string,
                  currentRowData: Record<string, any>
                ) => {
                  const dependentFields = config.childControls?.filter(
                    (c: any) => {
                      const expr = c.ExpressionV2;
                      if (!expr) return false;
                      const childFieldBindValue = c.name || c.BindValue;
                      const dependsOnField =
                        expr.includes(`[${fieldNameToCheck}]`) ||
                        expr.includes(`[${childFieldBindValue}]`);
                      // Only evaluate if not already evaluated in this cycle
                      return (
                        dependsOnField &&
                        !evaluatedFields.has(childFieldBindValue)
                      );
                    }
                  );

                  if (dependentFields && dependentFields.length > 0) {
                    // Evaluate each dependent field synchronously
                    dependentFields.forEach((depControl: any) => {
                      const depFieldName =
                        depControl.name || depControl.BindValue;

                      // Skip if already evaluated
                      if (evaluatedFields.has(depFieldName)) {
                        return;
                      }

                      evaluatedFields.add(depFieldName);

                      const depFieldValue = currentRowData[depFieldName];
                      const depExpression = depControl.ExpressionV2;

                      if (depExpression && typeof depExpression === "string") {
                        // Resolve all references in the expression
                        const fieldRefRegex = /\[[\w.()]*\]/g;
                        const matches =
                          depExpression.match(fieldRefRegex) || [];
                        let expression = depExpression;
                        let canEvaluate = true;

                        for (const match of matches) {
                          const resolvedValue = resolveFieldReference(
                            match,
                            currentRowData
                          );
                          if (resolvedValue !== null) {
                            expression = expression.replace(
                              match,
                              resolvedValue
                            );
                          } else {
                            canEvaluate = false;
                            break;
                          }
                        }

                        if (canEvaluate) {
                          try {
                            const evaluatedValue = Function(
                              `"use strict"; return (${expression})`
                            )();

                            if (
                              evaluatedValue !== null &&
                              evaluatedValue !== undefined
                            ) {
                              const resultValue =
                                evaluatedValue === ""
                                  ? ""
                                  : String(evaluatedValue);

                              // Update the row data
                              currentRowData[depFieldName] = resultValue;
                              if (depControl.Json_Attribute) {
                                currentRowData[depControl.Json_Attribute] = resultValue;
                              }

                              // Recursively evaluate fields that depend on this one
                              evaluateDependentFields(
                                depFieldName,
                                currentRowData
                              );
                            }
                          } catch (error) {
                            // Silently handle errors in recursive evaluation
                          }
                        }
                      }
                    });
                  }
                };

                // Start recursive evaluation
                evaluateDependentFields(childFieldName, newData[rowIndex]);
              }
            } catch (error) {
              // On error, set to default value (like old framework)
              if (childControlAny.defaultValue !== undefined) {
                newData[rowIndex] = {
                  ...newData[rowIndex],
                  [childFieldName]: childControlAny.defaultValue,
                };
                if (childControlAny.Json_Attribute) {
                  newData[rowIndex][childControlAny.Json_Attribute] = childControlAny.defaultValue;
                }
              }
            }


            // CRITICAL: If evaluation failed due to missing rawData, try to fetch it
            // This handles the case where user selects before rawData is loaded
            for (const match of matches) {
              const refValue = match.replace(/[\[\]]/g, "");
              if (refValue.includes(".")) {
                const [fieldName, property] = refValue.split(".");
                const refControl = config.childControls?.find(
                  (c: any) => (c.name || c.BindValue) === fieldName
                );

                if (refControl) {
                  const lookupName =
                    (refControl as any).LookupName ||
                    (refControl as any).lookupName;
                  if (lookupName) {
                    const parentField =
                      (refControl as any).Parent_Field ||
                      (refControl as any).parentField;
                    const actualParentField = parentField || config.parentField;
                    let lookupKey = lookupName;

                    if (actualParentField) {
                      const parentValue = formData[actualParentField];
                      if (
                        parentValue !== undefined &&
                        parentValue !== null &&
                        parentValue !== ""
                      ) {
                        lookupKey = `${lookupName}_${parentValue}`;
                      }
                    }

                    const rawData = lookupsState.rawData[lookupKey] || [];
                    const isRawDataLoading =
                      lookupsState.loading[lookupKey] || false;

                    const lastFetched = lookupsState.lastFetched[lookupKey];
                    const requestKey = `rawdata_evaluateExpression_${lookupKey}`;

                    if (rawData.length === 0 && !isRawDataLoading && !lastFetched && !requestedLookupsRef.current.has(requestKey)) {
                      requestedLookupsRef.current.add(requestKey);
                      dispatch(
                        fetchSubModuleRawData({
                          lookupName,
                          parentValue: actualParentField
                            ? formData[actualParentField] || ""
                            : "",
                          lookuptype:
                            refControl.componentType ===
                              ComponentType.SEARCHABLE_DROPDOWN
                              ? "Select"
                              : "Select",
                        })
                      );
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    handleDataChange(newData);
  };

  // Track parent field value for stable dependency
  const configAny = config as any;
  const actualParentField = configAny.Parent_Field || configAny.parentField || config.parentField;
  const parentFieldValueString = useMemo(() => {
    // Use the same utility as Report component to find parent value
    let currentParentValue = getParentIdFromFormData(formData, actualParentField);

    // If still not found and parentField is "parentid", also try estimate_id (for jobcard from estimation)
    if (!currentParentValue && actualParentField === "parentid") {
      currentParentValue = formData["estimate_id"] || formData["estimation_id"] || null;
    }

    return currentParentValue ? String(currentParentValue) : "";
  }, [formData, actualParentField]);

  // Clear requested lookups when parent field values change (for dependent lookups)
  useEffect(() => {
    // Clear dependent lookup requests when parent values change
    // This allows refetching when parent selection changes
    const currentParentValue = formData[actualParentField || ""];
    const currentParentValueString = String(currentParentValue || "");
    if (previousParentValueRef.current !== currentParentValueString) {
      // Clear all dependent lookup requests
      const keysToRemove: string[] = [];
      requestedLookupsRef.current.forEach((key) => {
        if (key.startsWith("dependent_") || key.startsWith("rawdata_")) {
          keysToRemove.push(key);
        }
      });
      keysToRemove.forEach((key) => requestedLookupsRef.current.delete(key));
      previousParentValueRef.current = currentParentValueString;
    }
  }, [parentFieldValueString, actualParentField]);

  // Fetch lookups for SubModule childControls
  useEffect(() => {
    // Create a stable string representation of childControls to detect actual changes
    const childControlsKey = config.childControls
      ? JSON.stringify(
        config.childControls.map((c: any) => ({
          lookupName: c.LookupName || c.lookupName,
          parentField: c.Parent_Field || c.parentField,
          componentType: c.componentType,
        }))
      )
      : "";

    // Only proceed if childControls actually changed (by content, not just reference)
    if (childControlsKey === previousChildControlsRef.current) {
      return;
    }
    previousChildControlsRef.current = childControlsKey;

    const fetchLookups = async () => {
      if (!config.childControls) return;

      for (const control of config.childControls) {
        const controlAny = control as any;
        const lookupName = controlAny.LookupName || controlAny.lookupName;
        if (!lookupName) continue;

        const parentField = controlAny.Parent_Field || controlAny.parentField;
        // Use control's parentField first, then fall back to config's parentField
        const actualParentField = parentField || config.parentField;

        if (actualParentField) {
          // Dependent lookup - need parent value
          const parentValue = formData[actualParentField];
          if (
            parentValue !== undefined &&
            parentValue !== null &&
            parentValue !== ""
          ) {
            const lookupKey = `${lookupName}_${parentValue}`;
            const requestKey = `dependent_${lookupKey}`;

            const isLoading = lookupsState.loading[lookupKey] || false;
            const lookupData = lookupsState.lookups[lookupKey];
            const existingOptions = Array.isArray(lookupData) ? lookupData : [];

            // CRITICAL FIX: Check if data already exists first, before checking ref
            // This prevents re-fetching when Redux state updates after a successful fetch
            if (Array.isArray(existingOptions) && existingOptions.length > 0) {
              // Data already exists, mark as requested to prevent future fetches
              if (!requestedLookupsRef.current.has(requestKey)) {
                requestedLookupsRef.current.add(requestKey);
              }
              // Skip fetching lookup data, but still check rawData
            } else if (
              !requestedLookupsRef.current.has(requestKey) &&
              !isLoading
            ) {
              // Only fetch if we haven't requested it and it's not loading
              requestedLookupsRef.current.add(requestKey);
              dispatch(
                fetchDependentLookup({
                  lookupName,
                  parentValue,
                  lookuptype:
                    control.componentType === ComponentType.SEARCHABLE_DROPDOWN
                      ? "Select"
                      : "Select",
                })
              );
            }

            // Also fetch rawData for ExpressionV2 to access all properties
            const rawDataKey = `${lookupName}_${parentValue}`;
            const rawDataRequestKey = `rawdata_${rawDataKey}`;
            const rawDataLookup = lookupsState.rawData[rawDataKey];
            const existingRawData = Array.isArray(rawDataLookup)
              ? rawDataLookup
              : [];
            const isRawDataLoading = lookupsState.loading[rawDataKey] || false;

            // CRITICAL FIX: Check if rawData already exists first
            if (Array.isArray(existingRawData) && existingRawData.length > 0) {
              // Raw data already exists, mark as requested
              if (!requestedLookupsRef.current.has(rawDataRequestKey)) {
                requestedLookupsRef.current.add(rawDataRequestKey);
              }
            } else if (
              !requestedLookupsRef.current.has(rawDataRequestKey) &&
              !isRawDataLoading
            ) {
              // Only fetch if we haven't requested it and it's not loading
              requestedLookupsRef.current.add(rawDataRequestKey);
              dispatch(
                fetchSubModuleRawData({
                  lookupName,
                  parentValue,
                  lookuptype:
                    control.componentType === ComponentType.SEARCHABLE_DROPDOWN
                      ? "Select"
                      : "Select",
                })
              );
            }
          }
        } else {
          // Independent lookup
          const lookupKey = lookupName;
          const requestKey = `independent_${lookupKey}`;

          const isLoading = lookupsState.loading[lookupKey] || false;
          const lookupData = lookupsState.lookups[lookupKey];
          const existingOptions = Array.isArray(lookupData) ? lookupData : [];
          const lastFetched = lookupsState.lastFetched[lookupKey];

          // Log cache check for journal_ledgers


          // CRITICAL FIX: Check if data already exists first, before checking ref
          // This prevents re-fetching when Redux state updates after a successful fetch
          if (Array.isArray(existingOptions) && existingOptions.length > 0) {
            // Data already exists, mark as requested to prevent future fetches
            if (!requestedLookupsRef.current.has(requestKey)) {
              requestedLookupsRef.current.add(requestKey);
            }

            // Skip fetching lookup data, but still check rawData
          } else if (
            !requestedLookupsRef.current.has(requestKey) &&
            !isLoading
          ) {
            // Only fetch if we haven't requested it and it's not loading
            requestedLookupsRef.current.add(requestKey);
            dispatch(
              fetchLookup({
                lookupName,
                params: {
                  parentid: "",
                  lookuptype:
                    control.componentType === ComponentType.SEARCHABLE_DROPDOWN
                      ? "Select"
                      : "Select",
                },
              })
            );
          }

          // CRITICAL FIX: Also fetch rawData for independent lookups
          // This ensures ExpressionV2 can access properties like purchase_rate, sale_rate, etc.
          const rawDataRequestKey = `rawdata_${lookupKey}`;
          const rawDataLookup = lookupsState.rawData[lookupKey];
          const existingRawData = Array.isArray(rawDataLookup)
            ? rawDataLookup
            : [];
          const isRawDataLoading = lookupsState.loading[lookupKey] || false;

          // CRITICAL FIX: Check if rawData already exists first
          if (Array.isArray(existingRawData) && existingRawData.length > 0) {
            // Raw data already exists, mark as requested
            if (!requestedLookupsRef.current.has(rawDataRequestKey)) {
              requestedLookupsRef.current.add(rawDataRequestKey);
            }
          } else if (
            !requestedLookupsRef.current.has(rawDataRequestKey) &&
            !isRawDataLoading
          ) {
            // Only fetch if we haven't requested it and it's not loading
            requestedLookupsRef.current.add(rawDataRequestKey);
            // For independent lookups, parentValue is empty string
            dispatch(
              fetchSubModuleRawData({
                lookupName,
                parentValue: "", // Empty for independent lookups
                lookuptype:
                  control.componentType === ComponentType.SEARCHABLE_DROPDOWN
                    ? "Select"
                    : "Select",
              })
            );
          }
        }
      }
    };

    fetchLookups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.childControls,
    actualParentField,
    dispatch,
    parentFieldValueString, // Use string value for stable comparison
    formData, // Also depend on formData to trigger when parent values are set
  ]);

  // CRITICAL: Also fetch lookups when data is populated (for prepopulated data)
  // This ensures dropdown options are available when rows are prepopulated from API or loaded in edit mode
  // This effect runs when data is loaded from value prop (edit mode) or when data state changes
  useEffect(() => {
    // Use value prop if available (edit mode), otherwise use data state
    // CRITICAL: Prefer value prop for edit mode, but also check data state in case value prop hasn't synced yet
    const dataToCheck = (value && Array.isArray(value) && value.length > 0) ? value : (data && Array.isArray(data) && data.length > 0 ? data : []);

    // Only trigger if we have data (prepopulated) and lookups might not be loaded yet
    if (!dataToCheck || dataToCheck.length === 0) {

      return;
    }



    const fetchLookupsForPopulatedData = async () => {
      if (!config.childControls) return;

      // CRITICAL: For edit mode, fetch ALL dropdown lookups, not just ones with values
      // This ensures all dropdowns have their options available when data is loaded
      const hasData = dataToCheck && dataToCheck.length > 0;

      for (const control of config.childControls) {
        const controlAny = control as any;
        const lookupName = controlAny.LookupName || controlAny.lookupName;
        if (!lookupName) continue;

        // Check if this is a dropdown component
        const controlType = (control as any).componentType;
        const isDropdown =
          controlType === ComponentType.SELECT_DROPDOWN ||
          controlType === ComponentType.SEARCHABLE_DROPDOWN ||
          controlType === "selectDropdown" ||
          controlType === "searchableDropdown";

        if (!isDropdown) continue; // Only fetch for dropdowns

        // Check if this control has values in any row
        // CRITICAL: Check both BindValue (for user input) and Json_Attribute (for API data in edit mode)
        let hasValueInRows = false;
        let foundValue: any = null;

        if (hasData) {
          for (const row of dataToCheck) {
            const fieldName = controlAny.name || controlAny.BindValue;
            const jsonAttribute = controlAny.Json_Attribute;

            // Try BindValue first (for add mode where user enters data)
            let fieldValue = row[fieldName];

            // If not found and Json_Attribute exists, try Json_Attribute (for edit mode backend data)
            if ((fieldValue === undefined || fieldValue === null || fieldValue === "") && jsonAttribute) {
              fieldValue = row[jsonAttribute];
            }

            // Also try case-insensitive and underscore variations for robustness
            if ((fieldValue === undefined || fieldValue === null || fieldValue === "") && jsonAttribute) {
              const possibleKeys = [
                jsonAttribute.toLowerCase(),
                jsonAttribute.toUpperCase(),
                jsonAttribute.replace(/_/g, ""),
                fieldName.toLowerCase(),
                fieldName.toUpperCase(),
              ];
              for (const key of possibleKeys) {
                if (row[key] !== undefined && row[key] !== null && row[key] !== "") {
                  fieldValue = row[key];
                  break;
                }
              }
            }

            if (fieldValue !== undefined && fieldValue !== null && fieldValue !== "") {
              hasValueInRows = true;
              foundValue = fieldValue;
              break; // Found a value, no need to check other rows
            }
          }
        }

        // CRITICAL: In edit mode (hasData), always fetch lookup options for ALL dropdowns
        // This ensures dropdowns can display values even if we haven't found the value yet
        // (the value might be in a different format or we might have missed it)
        // Only skip if there's no data at all (not in edit mode)
        if (!hasData) {

          continue; // Skip if no data at all (not in edit mode)
        }

        // If we have data but didn't find a value, still fetch the lookup
        // The value might be there but in a format we didn't detect
        if (!hasValueInRows && hasData) {

          // Set foundValue to null so we still fetch, but don't check for value existence
          foundValue = null;
        }



        // CRITICAL: Only use parentField from the control itself, NOT from config.parentField
        // Config.parentField is for SubModule-level lookups, not individual control lookups
        // Using config.parentField as fallback causes independent lookups to be treated as dependent
        const parentField = controlAny.Parent_Field || controlAny.parentField;

        if (parentField) {
          // Dependent lookup - control has its own parentField
          const parentValue = formData[parentField];
          if (
            parentValue !== undefined &&
            parentValue !== null &&
            parentValue !== ""
          ) {
            const lookupKey = `${lookupName}_${parentValue}`;
            const requestKey = `dependent_prepop_${lookupKey}`;

            // CRITICAL: Get current state values inside the effect (not from dependencies)
            // This prevents infinite loops when lookupsState changes
            const currentLookupsState = lookupsState;
            const existingOptions = currentLookupsState.lookups[lookupKey] || [];
            const isLoading = currentLookupsState.loading[lookupKey] || false;
            const alreadyRequested = requestedLookupsRef.current.has(requestKey);

            // CRITICAL: Only fetch if:
            // 1. Options are empty
            // 2. Not currently loading
            // 3. Not already requested (prevents duplicate fetches)
            const shouldFetch = existingOptions.length === 0 && !isLoading && !alreadyRequested;

            // Also check if the value exists in the options - if not, might need to re-fetch
            const valueExists = existingOptions.length > 0 && foundValue ? existingOptions.some((opt: any) =>
              String(opt.value) === String(foundValue) ||
              String(opt.id) === String(foundValue)
            ) : false;

            if (shouldFetch) {

              requestedLookupsRef.current.add(requestKey);
              dispatch(
                fetchDependentLookup({
                  lookupName,
                  parentValue: String(parentValue),
                  lookuptype:
                    control.componentType === ComponentType.SEARCHABLE_DROPDOWN
                      ? "Select"
                      : "Select",
                })
              );

            }
          } else {
            // Independent lookup
            const lookupKey = lookupName;
            const requestKey = `independent_prepop_${lookupKey}`;

            // CRITICAL: Get current state values inside the effect (not from dependencies)
            // This prevents infinite loops when lookupsState changes
            const currentLookupsState = lookupsState;
            const existingOptions = currentLookupsState.lookups[lookupKey] || [];
            const isLoading = currentLookupsState.loading[lookupKey] || false;
            const alreadyRequested = requestedLookupsRef.current.has(requestKey);

            // CRITICAL: Only fetch if:
            // 1. Options are empty
            // 2. Not currently loading
            // 3. Not already requested (prevents duplicate fetches)
            const shouldFetch = existingOptions.length === 0 && !isLoading && !alreadyRequested;

            // Also check if the value exists in the options - if not, might need to re-fetch
            const valueExists = existingOptions.length > 0 && foundValue ? existingOptions.some((opt: any) =>
              String(opt.value) === String(foundValue) ||
              String(opt.id) === String(foundValue)
            ) : false;

            if (shouldFetch) {

              requestedLookupsRef.current.add(requestKey);
              dispatch(
                fetchLookup({
                  lookupName,
                  params: {
                    lookuptype:
                      control.componentType === ComponentType.SEARCHABLE_DROPDOWN
                        ? "Select"
                        : "Select",
                  },
                })
              );

            }
          }
        }
      }
    };

    fetchLookupsForPopulatedData();
    // CRITICAL: Do NOT include lookupsState.lookups or lookupsState.loading in dependencies
    // They cause infinite loops because the effect re-runs every time a lookup loads
    // Instead, check these values inside the effect using the current state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, value, formData, config.childControls, dispatch]);

  // CRITICAL: Force re-render when lookup options finish loading
  // This ensures dropdowns update when options become available
  useEffect(() => {
    // This effect doesn't do anything, but by depending on lookupsState.lookups,
    // it forces a re-render when lookups are loaded, which causes renderField to run again
    // and check if values can now be matched with the newly loaded options

    // CRITICAL: Do NOT include lookupsState.lookups in dependencies
    // It causes infinite loops because the effect re-runs every time any lookup loads
    // This effect only logs, so it doesn't need to re-run when lookups change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // CRITICAL: Watch for when lookup options become available and re-check if we need to fetch
  // This ensures dropdowns get their options even if they load after the data
  useEffect(() => {
    // Use value prop if available (edit mode), otherwise use data state
    const dataToCheck = (value && Array.isArray(value) && value.length > 0) ? value : data;

    // Only trigger if we have data
    if (!dataToCheck || dataToCheck.length === 0) return;
    if (!config.childControls) return;

    // Check each dropdown field to see if options are now available
    for (const control of config.childControls) {
      const controlAny = control as any;
      const lookupName = controlAny.LookupName || controlAny.lookupName;
      if (!lookupName) continue;

      // Check if this is a dropdown component
      const controlType = (control as any).componentType;
      const isDropdown =
        controlType === ComponentType.SELECT_DROPDOWN ||
        controlType === ComponentType.SEARCHABLE_DROPDOWN ||
        controlType === "selectDropdown" ||
        controlType === "searchableDropdown";

      if (!isDropdown) continue;

      // Check if this control has values in any row
      let foundValue: any = null;
      for (const row of dataToCheck) {
        const fieldName = controlAny.name || controlAny.BindValue;
        const jsonAttribute = controlAny.Json_Attribute;

        let fieldValue = row[fieldName];
        if ((fieldValue === undefined || fieldValue === null || fieldValue === "") && jsonAttribute) {
          fieldValue = row[jsonAttribute];
        }

        if (fieldValue !== undefined && fieldValue !== null && fieldValue !== "") {
          foundValue = fieldValue;
          break;
        }
      }

      if (!foundValue) continue;

      const parentField = controlAny.Parent_Field || controlAny.parentField;
      const actualParentField = parentField || config.parentField;

      if (actualParentField) {
        const parentValue = formData[actualParentField];
        if (parentValue !== undefined && parentValue !== null && parentValue !== "") {
          const lookupKey = `${lookupName}_${parentValue}`;
          const existingOptions = lookupsState.lookups[lookupKey] || [];

          // If options are now available, log success

        }
      } else {
        const lookupKey = lookupName;
        const existingOptions = lookupsState.lookups[lookupKey] || [];

        // If options are now available, log success

      }
    }
    // CRITICAL: Do NOT include lookupsState.lookups in dependencies
    // It causes infinite loops because the effect re-runs every time any lookup loads
    // We check lookupsState.lookups inside the effect instead
    // Also removed data and value to prevent loops when they change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, config.childControls]);

  // CRITICAL: Re-evaluate ExpressionV2 when lookup rawData becomes available
  // This ensures fields like [drp_code.sale_rate] are calculated when lookup data loads
  // This is especially important in edit mode where data loads before lookup data
  useEffect(() => {
    // Only re-evaluate if we have data and ExpressionV2 fields that depend on lookup properties
    const hasData = (value && value.length > 0) || (data && data.length > 0);
    if (!hasData) {
      // Reset processed keys when data is cleared
      processedRawDataKeysRef.current.clear();
      return;
    }

    // Check if any childControl has ExpressionV2 with property lookups (e.g., [drp_code.sale_rate])
    const propertyLookupControls = config.childControls?.filter((control) => {
      const controlAny = control as any;
      const expression = controlAny.ExpressionV2;
      if (!expression || typeof expression !== "string") return false;
      // Check if expression contains property access (e.g., [field.property])
      return /\[[\w]+\.[\w]+\]/.test(expression);
    }) || [];

    if (propertyLookupControls.length === 0) return;

    // Get current rawData state inside the effect (not from dependencies)
    const currentRawData = lookupsState.rawData;

    // For each property lookup control, check if its referenced lookup's rawData has loaded
    let allLookupsLoaded = true;
    let needsReEvaluation = false;

    for (const control of propertyLookupControls) {
      const controlAny = control as any;
      const expression = controlAny.ExpressionV2;
      if (!expression || typeof expression !== "string") continue;

      // Extract field references with properties (e.g., [drp_code.sale_rate])
      const propertyRefRegex = /\[([\w]+)\.([\w]+)\]/g;
      const matches = expression.matchAll(propertyRefRegex);

      for (const match of matches) {
        const fieldName = match[1]; // e.g., "drp_code"
        const property = match[2]; // e.g., "sale_rate"

        // Find the control that this field references
        const refControl = config.childControls?.find(
          (c: any) => (c.name || c.BindValue) === fieldName
        );

        if (!refControl) continue;

        const lookupName = (refControl as any).LookupName || (refControl as any).lookupName;
        if (!lookupName) continue;

        // Determine lookup key
        const parentFieldForLookup =
          (refControl as any).Parent_Field ||
          (refControl as any).parentField ||
          config.parentField;
        let lookupKey = lookupName;

        if (parentFieldForLookup) {
          const parentValue = formData[parentFieldForLookup];
          if (
            parentValue !== undefined &&
            parentValue !== null &&
            parentValue !== ""
          ) {
            lookupKey = `${lookupName}_${parentValue}`;
          }
        }

        // Check if rawData is loaded for this lookup
        // CRITICAL: Get current state values inside the effect (not from dependencies)
        // This prevents infinite loops when lookupsState changes
        const rawData = currentRawData[lookupKey] || [];
        const isLoading = lookupsState.loading[lookupKey] || false;
        const requestKey = `rawdata_reeval_${lookupKey}`;
        const alreadyRequested = requestedLookupsRef.current.has(requestKey);

        if (rawData.length === 0) {
          allLookupsLoaded = false;
          // Check if we need to fetch it
          // CRITICAL: Only fetch if not loading and not already requested
          if (!isLoading && !alreadyRequested) {

            requestedLookupsRef.current.add(requestKey);
            dispatch(
              fetchSubModuleRawData({
                lookupName,
                parentValue: parentFieldForLookup ? (formData[parentFieldForLookup] || "") : "",
                lookuptype: (refControl as any).componentType === ComponentType.SEARCHABLE_DROPDOWN ? "Select" : "Select",
              })
            );
          }
        } else {
          // RawData is loaded, check if we've already processed this lookup key
          // This prevents re-evaluating when the same rawData loads multiple times
          const hasProcessedThisKey = processedRawDataKeysRef.current.has(lookupKey);

          // RawData is loaded, check if we have values in rows that need this property
          const dataSource = value && value.length > 0 ? value : data;
          const hasValuesNeedingProperty = dataSource.some((row: any) => {
            const fieldValue = row[fieldName] || row[(refControl as any).Json_Attribute];
            return fieldValue !== undefined && fieldValue !== null && fieldValue !== "";
          });

          // Only mark for re-evaluation if we haven't processed this key yet
          if (hasValuesNeedingProperty && !hasProcessedThisKey) {
            needsReEvaluation = true;

            // Also check if the current value in the row is different from what it should be
            // This helps detect if ExpressionV2 needs to be re-evaluated
            const controlWithExpression = propertyLookupControls.find((c: any) => {
              const expr = (c as any).ExpressionV2;
              return expr && typeof expr === "string" && expr.includes(`[${fieldName}.${property}]`);
            });

            if (controlWithExpression) {
              const expressionFieldName = (controlWithExpression as any).name || (controlWithExpression as any).BindValue;
              const expressionJsonAttr = (controlWithExpression as any).Json_Attribute;

              // Check if any row has a zero or empty value for the expression field
              // This indicates it might need recalculation
              const needsRecalc = dataSource.some((row: any) => {
                const currentValue = row[expressionFieldName] || row[expressionJsonAttr];
                return currentValue === undefined || currentValue === null || currentValue === "" || currentValue === "0" || currentValue === 0;
              });

              if (needsRecalc) {
                needsReEvaluation = true;
              }
            }
          }
        }
      }
    }

    // Only re-evaluate if all required lookups are loaded and we have data that needs it
    if (!allLookupsLoaded || !needsReEvaluation) {

      return;
    }

    // Re-evaluate ExpressionV2 for all rows
    const dataSource = value && value.length > 0 ? value : data;
    const rowsWithReEvaluatedExpressions = dataSource.map((row: any, index: number) => {
      return evaluateAllExpressionsForRow(row, index);
    });

    // CRITICAL: Only update if values actually changed AND the new values are better (not "0" overwriting real values)
    // This prevents overwriting correct values that were loaded from backend
    let shouldUpdate = false;
    let hasImprovements = false;

    for (let i = 0; i < dataSource.length; i++) {
      const oldRow = dataSource[i];
      const newRow = rowsWithReEvaluatedExpressions[i];

      // Check each ExpressionV2 field
      for (const control of propertyLookupControls) {
        const controlAny = control as any;
        const expressionFieldName = controlAny.name || controlAny.BindValue;
        const expressionJsonAttr = controlAny.Json_Attribute;

        const oldValue = oldRow[expressionFieldName] || oldRow[expressionJsonAttr];
        const newValue = newRow[expressionFieldName] || newRow[expressionJsonAttr];

        // If old value exists and is not "0", and new value is "0", don't overwrite
        if (oldValue !== undefined && oldValue !== null && oldValue !== "" && oldValue !== "0" && oldValue !== 0) {
          if (newValue === "0" || newValue === 0 || newValue === "" || newValue === null || newValue === undefined) {
            // Preserve old value - don't overwrite with "0"
            newRow[expressionFieldName] = oldValue;
            if (expressionJsonAttr && !newRow[expressionFieldName]) {
              newRow[expressionJsonAttr] = oldValue;
            }

          } else if (oldValue !== newValue) {
            // New value is different and not "0", this is an improvement
            hasImprovements = true;
          }
        } else if (newValue !== oldValue && newValue !== "0" && newValue !== 0 && newValue !== "" && newValue !== null && newValue !== undefined) {
          // Old value was "0" or empty, new value is not - this is an improvement
          hasImprovements = true;
        }
      }
    }

    const currentDataString = JSON.stringify(dataSource);
    const newDataString = JSON.stringify(rowsWithReEvaluatedExpressions);
    shouldUpdate = currentDataString !== newDataString && hasImprovements;

    if (shouldUpdate) {


      // Mark all processed lookup keys to prevent re-processing
      for (const control of propertyLookupControls) {
        const controlAny = control as any;
        const lookupName = (controlAny as any).LookupName || (controlAny as any).lookupName;
        if (lookupName) {
          const parentFieldForLookup =
            (controlAny as any).Parent_Field ||
            (controlAny as any).parentField ||
            config.parentField;
          let lookupKey = lookupName;
          if (parentFieldForLookup) {
            const parentValue = formData[parentFieldForLookup];
            if (parentValue !== undefined && parentValue !== null && parentValue !== "") {
              lookupKey = `${lookupName}_${parentValue}`;
            }
          }
          processedRawDataKeysRef.current.add(lookupKey);
        }
      }

      isUpdatingFromInternalRef.current = true;
      setData(rowsWithReEvaluatedExpressions);
      const validationError = validateData(rowsWithReEvaluatedExpressions);
      setError(validationError);
      onChangeRef.current?.(config.name || (config as any).BindValue, rowsWithReEvaluatedExpressions);
    }

    // CRITICAL: Do NOT include lookupsState.rawData in dependencies
    // It causes infinite loops because the object reference changes every time any lookup loads
    // We check rawData inside the effect and use processedRawDataKeysRef to prevent re-processing
    // Also do NOT include data and value - they cause loops when the effect updates data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, config.childControls, config.name]);

  // Auto-populate SubModule rows from lookupName when parentField changes
  // Flow: User Selects Parent Field → SubModule detects change → GetLookupItems() → Backend API call → Returns list → SetLookupData() creates rows
  useEffect(() => {
    const lookupName = configAny.LookupName || configAny.lookupName || config.lookupName;
    const parentField = actualParentField;



    // Only proceed if both lookupName and parentField are configured
    if (!lookupName || !parentField) {

      return;
    }

    // Step 1: Get parent field value using the same utility as Report component
    // This handles all the edge cases and variations
    // CRITICAL: Check parent value FIRST before checking for existing data
    // This ensures parent change detection works even when data exists
    let parentValue: any = getParentIdFromFormData(formData, parentField);

    // If still not found and parentField is "parentid", also try estimate_id (for jobcard from estimation)
    if (!parentValue && parentField === "parentid") {
      parentValue = formData["estimate_id"] || formData["estimation_id"] || null;

    }

    // Convert to string for consistency
    parentValue = parentValue ? String(parentValue) : parentValue;

    // Debug logging


    // Step 2: Check if parent value has changed (SubModule detected change)
    // CRITICAL: Check this BEFORE checking for existing data
    // This ensures we clear data when parent changes, even if data exists
    const parentValueChanged =
      previousParentValueRef.current !== parentValue &&
      previousParentValueRef.current !== undefined;

    // CRITICAL FIX: Always clear data when parent value changes to a different value
    // This ensures that when user changes jobcard in add mode, old data is cleared
    // and new data is fetched for the new jobcard
    // Only skip clearing on initial load (when previousParentValueRef is undefined)
    if (parentValueChanged) {
      // Always clear when parent changes (user changed jobcard)
      // This prevents old data from persisting when switching between jobcards

      isUpdatingFromInternalRef.current = true;
      setData([]);
      lastPopulatedDataRef.current = "";
      lastValueRef.current = JSON.stringify([]);
      lastLookupDataRef.current = [];
      // Clear the processed parent value ref to force re-fetch of lookup data
      // CRITICAL: Set to undefined to ensure fetch logic treats this as a new parent
      lastProcessedParentValueRef.current = undefined;
      // Clear any requested lookups for both old and new parent to allow re-fetching
      const oldLookupKey = `${lookupName}_${previousParentValueRef.current}`;
      const newLookupKey = `${lookupName}_${parentValue}`;
      const oldRequestKey = `submodule_${oldLookupKey}`;
      const newRequestKey = `submodule_${newLookupKey}`;
      // Clear all related lookup request flags
      requestedLookupsRef.current.delete(oldLookupKey);
      requestedLookupsRef.current.delete(`rawdata_${oldLookupKey}`);
      requestedLookupsRef.current.delete(oldRequestKey);
      requestedLookupsRef.current.delete(newLookupKey);
      requestedLookupsRef.current.delete(`rawdata_${newLookupKey}`);
      requestedLookupsRef.current.delete(newRequestKey);
      // Clear the lookup data from Redux store for old parent (optional, but helps)
      // Note: We can't directly modify Redux state here, but clearing the refs ensures fresh fetch
      onChangeRef.current?.(config.name, []);


    }

    // Update the ref to track previous value BEFORE checking for existing data
    // This ensures the next change is detected correctly
    previousParentValueRef.current = parentValue;

    // IMPORTANT: Don't auto-populate if data already exists (from value prop in edit mode)
    // BUT: Only skip if parent hasn't changed (edit mode with same parent)
    // If parent changed, we already cleared data above, so proceed with fetch
    const hasExistingData = value && Array.isArray(value) && value.length > 0;
    if (hasExistingData && !parentValueChanged) {
      // Data already exists from value prop and parent hasn't changed, skip auto-population
      // This is edit mode with same parent

      return;
    }

    // Only proceed if parent value exists
    if (
      parentValue === undefined ||
      parentValue === null ||
      parentValue === ""
    ) {
      // Clear data if parent value is cleared
      // Only preserve data if this is initial load with data from value prop (edit mode)
      const hasExistingDataFromValue =
        value && Array.isArray(value) && value.length > 0;
      const isInitialLoad = previousParentValueRef.current === undefined;

      if (data.length > 0 && !(isInitialLoad && hasExistingDataFromValue)) {

        isUpdatingFromInternalRef.current = true;
        setData([]);
        lastPopulatedDataRef.current = "";
        lastValueRef.current = JSON.stringify([]);
        lastLookupDataRef.current = [];
        onChangeRef.current?.(config.name, []);
      }
      return;
    }

    // Step 3: GetLookupItems("issued_Spareparts_details", IssueNoID)
    const lookupKey = `${lookupName}_${parentValue}`;
    // Use rawData for SubModule to access all fields without transformation
    // This contains the list returned from the backend API call
    const lookupData = lookupsState.rawData[lookupKey] || [];
    const isLoading = lookupsState.loading[lookupKey] || false;

    // Step 4: Check if lookup data has changed (SubModule.OnParametersSetAsync() detects change)
    const lookupDataChanged =
      JSON.stringify(lastLookupDataRef.current) !== JSON.stringify(lookupData);

    // Update ref to track lookup data
    if (lookupDataChanged) {
      lastLookupDataRef.current = lookupData;
    }

    // For SubModule, always fetch raw data directly from API to preserve all fields
    // Don't rely on transformed lookup data from Redux store
    const fetchRawLookupData = async () => {
      try {
        const endpoint = `/pabws/utility/${lookupName}`;
        const payload = {
          parentid: String(parentValue),
          lookuptype: "",
        };

        const response = await apiService.post<any>(endpoint, payload, {
          includeAuthToken: true,
          errorHandling: "return-empty",
        });

        // Extract raw data array from response
        let rawData: any[] = [];
        if (response) {
          if (response.data && Array.isArray(response.data)) {
            rawData = response.data;
          } else if (Array.isArray(response)) {
            rawData = response;
          } else if (response.success && response.data) {
            rawData = Array.isArray(response.data)
              ? response.data
              : [response.data];
          }
        }

        // Transform raw data into rows
        // CRITICAL: Clear data if lookup returns empty array (new vendor has no data)
        if (rawData.length === 0) {

          isUpdatingFromInternalRef.current = true;
          setData([]);
          lastPopulatedDataRef.current = "";
          lastValueRef.current = JSON.stringify([]);
          onChangeRef.current?.(config.name, []);
          return;
        }

        if (rawData.length > 0) {
          const transformedRows = rawData.map((item: any) => {
            const row: Record<string, any> = {};

            config.childControls?.forEach((control: any) => {
              const fieldName = control.name || control.BindValue;
              const jsonAttribute = control.Json_Attribute;

              if (fieldName && jsonAttribute) {
                // Try to get value from raw item using Json_Attribute
                let value =
                  item[jsonAttribute] ||
                  item[jsonAttribute.toLowerCase()] ||
                  item[jsonAttribute.toUpperCase()] ||
                  item[jsonAttribute.replace(/_/g, "")] ||
                  "";

                // If no value found, try using fieldName as fallback
                if (!value) {
                  value =
                    item[fieldName] ||
                    item[fieldName.toLowerCase()] ||
                    item[fieldName.toUpperCase()] ||
                    "";
                }

                // Handle special cases for dropdown fields
                if (
                  (control.componentType === ComponentType.SELECT_DROPDOWN ||
                    control.componentType === ComponentType.SEARCHABLE_DROPDOWN) &&
                  control.lookupName
                ) {
                  // For dropdown fields in auto-populated SubModule:
                  // Keep the Json_Attribute value (codeid) as it should match the lookup options' value
                  // Only use id/value as fallback if Json_Attribute value is not available
                  // This ensures the stored value matches what's in the lookup options, so the dropdown can find the correct option and display the label
                  if (!value || value === "") {
                    // IMPORTANT: Do NOT blindly fall back to item.id for missing dropdown values.
                    // Example: Purchase with order screen (140) has a Jobcard dropdown (job_card_id),
                    // but purchase_order_details rows have their own id; using that id here makes it look
                    // like a "jobcard value is auto-generated". Only use id/value fallback when the
                    // configured Json_Attribute is actually "id".
                    if (jsonAttribute === "id") {
                      value = item.id || item.value || value;
                    }
                  }
                  // Ensure value is string for consistency with lookup options
                  value = value ? String(value) : value;
                }

                // Apply default value if no value found
                if (!value && control.defaultValue !== undefined) {
                  value = control.defaultValue;

                  // CRITICAL: If default value is a label (like "5%") and this is a lookup dropdown,
                  // convert it to the ID before storing
                  if (
                    (control.componentType === ComponentType.SELECT_DROPDOWN ||
                      control.componentType === ComponentType.SEARCHABLE_DROPDOWN) &&
                    (control.lookupName || control.LookupName)
                  ) {
                    const lookupName = control.lookupName || control.LookupName;
                    if (lookupName) {
                      const parentFieldForLookup =
                        control.Parent_Field ||
                        control.parentField ||
                        config.parentField;
                      let lookupKey = lookupName;

                      if (parentFieldForLookup) {
                        const parentValue = formData[parentFieldForLookup];
                        if (
                          parentValue !== undefined &&
                          parentValue !== null &&
                          parentValue !== ""
                        ) {
                          lookupKey = `${lookupName}_${parentValue}`;
                        }
                      }

                      const lookupOptions = lookupsState.lookups[lookupKey] || [];

                      // Check if the default value matches a label instead of an ID
                      const matchingOption = lookupOptions.find(
                        (opt: any) =>
                          opt.label === value ||
                          String(opt.label) === String(value) ||
                          (opt as any).name === value ||
                          String((opt as any).name) === String(value)
                      );

                      if (matchingOption) {
                        // Convert label to ID
                        value =
                          matchingOption.value || (matchingOption as any).id || value;


                      }
                    }
                  }
                }

                row[fieldName] = value;
              } else if (fieldName) {
                // If no Json_Attribute, try to match by fieldName
                row[fieldName] =
                  item[fieldName] ||
                  item[fieldName.toLowerCase()] ||
                  control.defaultValue ||
                  "";
              }
            });

            return row;
          });

          // Evaluate all ExpressionV2 fields for each row after transformation
          const rowsWithExpressions = transformedRows.map(
            (row: any, index: number) => {
              return evaluateAllExpressionsForRow(row, index);
            }
          );

          // Only update if parent value changed, lookup data changed, or data is different (to avoid infinite loops)
          const newDataString = JSON.stringify(rowsWithExpressions);
          if (lastPopulatedDataRef.current !== newDataString) {

            isUpdatingFromInternalRef.current = true;
            setData(rowsWithExpressions);
            const validationError = validateData(rowsWithExpressions);
            setError(validationError);
            lastValueRef.current = newDataString;
            onChangeRef.current?.(config.name, rowsWithExpressions);
            lastPopulatedDataRef.current = newDataString;
          }
        } else {

          isUpdatingFromInternalRef.current = true;
          setData([]);
          lastValueRef.current = JSON.stringify([]);
          onChangeRef.current?.(config.name, []);
          lastPopulatedDataRef.current = "";
        }
      } catch (error) {

        isUpdatingFromInternalRef.current = true;
        setData([]);
        lastValueRef.current = JSON.stringify([]);
        onChangeRef.current?.(config.name, []);
      }
    };

    // Step 5: If lookup data is available and not loading, populate rows (SetLookupData() creates rows)
    // IMPORTANT: Only auto-populate from lookup if data doesn't already exist (from value prop)
    // In edit mode, data should come from value prop, not from lookup auto-population
    // Note: We already checked hasExistingData at the beginning and returned early if it exists
    // This check is for the lookupData path - only populate if no existing data in state
    const hasExistingDataInState =
      data && Array.isArray(data) && data.length > 0;

    if (lookupData.length > 0 && !isLoading && !hasExistingDataInState) {
      // Step 6: Transform lookup data into rows based on childControls mapping
      // Each row populated with: Code, Item Name, Qty, Rate, etc.
      const transformedRows = lookupData.map((lookupItem: any) => {
        const row: Record<string, any> = {};

        config.childControls?.forEach((control: any) => {
          const fieldName = control.name || control.BindValue;
          const jsonAttribute = control.Json_Attribute;

          if (fieldName && jsonAttribute) {
            // Try to get value from lookup item using Json_Attribute
            let value =
              lookupItem[jsonAttribute] ||
              lookupItem[jsonAttribute.toLowerCase()] ||
              lookupItem[jsonAttribute.toUpperCase()] ||
              lookupItem[jsonAttribute.replace(/_/g, "")] ||
              "";

            // If no value found, try using fieldName as fallback
            if (!value) {
              value =
                lookupItem[fieldName] ||
                lookupItem[fieldName.toLowerCase()] ||
                lookupItem[fieldName.toUpperCase()] ||
                "";
            }

            // Handle special cases for dropdown fields
                if (
                  (control.componentType === ComponentType.SELECT_DROPDOWN ||
                    control.componentType === ComponentType.SEARCHABLE_DROPDOWN) &&
                  control.lookupName
                ) {
              // For dropdown fields in auto-populated SubModule:
              // Keep the Json_Attribute value (codeid) as it should match the lookup options' value
              // Only use id/value as fallback if Json_Attribute value is not available
              // This ensures the stored value matches what's in the lookup options, so the dropdown can find the correct option and display the label
                  if (!value || value === "") {
                    // Same rule as above: only fall back to id/value if Json_Attribute is "id".
                    // Otherwise keep empty to avoid injecting unrelated row ids into dropdown fields.
                    if (jsonAttribute === "id") {
                      value = lookupItem.id || lookupItem.value || value;
                    }
                  }
              // Ensure value is string for consistency with lookup options
              value = value ? String(value) : value;
            }

            // Apply default value if no value found
            if (!value && control.defaultValue !== undefined) {
              value = control.defaultValue;
            }

            row[fieldName] = value;
          } else if (fieldName) {
            // If no Json_Attribute, try to match by fieldName
            row[fieldName] =
              lookupItem[fieldName] ||
              lookupItem[fieldName.toLowerCase()] ||
              control.defaultValue ||
              "";
          }
        });

        return row;
      });

      // Only update if parent value changed, lookup data changed, or data is different (to avoid infinite loops)
      const newDataString = JSON.stringify(transformedRows);
      const shouldUpdate =
        parentValueChanged ||
        lookupDataChanged ||
        lastPopulatedDataRef.current !== newDataString;

      if (shouldUpdate) {
        // Evaluate all ExpressionV2 fields for each row after transformation
        const rowsWithExpressions = transformedRows.map(
          (row: any, index: number) => {
            return evaluateAllExpressionsForRow(row, index);
          }
        );

        const evaluatedDataString = JSON.stringify(rowsWithExpressions);
        lastPopulatedDataRef.current = evaluatedDataString;
        // Force state update - always set the new data
        isUpdatingFromInternalRef.current = true;
        setData(rowsWithExpressions);
        const validationError = validateData(rowsWithExpressions);
        setError(validationError);
        lastValueRef.current = evaluatedDataString;
        onChangeRef.current?.(config.name, rowsWithExpressions);
      } else {

      }
    } else if (!isLoading && lookupData.length === 0 && parentValue) {
      // Step 3: GetLookupItems() - Backend API call with parentid parameter
      // If lookup is not loading and has no data, fetch raw data
      // CRITICAL: Only fetch if parent value exists and we haven't already requested it
      const lookupKey = `${lookupName}_${parentValue}`;
      const requestKey = `submodule_${lookupKey}`;

      // CRITICAL: Only fetch if parent value actually changed OR this is the first time we see this parent value
      // This prevents infinite loops when rawData updates trigger the effect
      // But allows initial fetch when screen loads with parent value already set
      const parentValueChangedForFetch =
        lastProcessedParentValueRef.current !== parentValue;
      const isInitialFetch = lastProcessedParentValueRef.current === undefined;

      // Check if we have data for this lookup
      const hasDataForThisLookup = lookupsState.rawData[lookupKey] && lookupsState.rawData[lookupKey].length > 0;
      const isStillLoading = lookupsState.loading[lookupKey] || false;

      // CRITICAL: Only clear request flag if parent value changed (new parent = need new data)
      // Don't clear if same parent - this prevents infinite loops when API returns empty or fails
      // If API fails for same parent, we don't want to keep retrying
      const shouldClearRequestFlag =
        requestedLookupsRef.current.has(requestKey) &&
        !hasDataForThisLookup &&
        !isStillLoading &&
        parentValueChangedForFetch; // Only clear if parent changed

      if (shouldClearRequestFlag) {

        requestedLookupsRef.current.delete(requestKey);
      }

      // Check if we've already requested this lookup to prevent infinite loops
      // CRITICAL: Only fetch if parent value changed OR it's initial fetch OR we don't have data and parent hasn't changed
      // But don't fetch if we already requested for this parent value
      if (
        (parentValueChangedForFetch || isInitialFetch) &&
        !requestedLookupsRef.current.has(requestKey)
      ) {
        requestedLookupsRef.current.add(requestKey);
        lastProcessedParentValueRef.current = parentValue;

        // Clear data if it exists (new parent has no data yet)
        if (data.length > 0 && !hasExistingData) {

          isUpdatingFromInternalRef.current = true;
          setData([]);
          lastPopulatedDataRef.current = "";
          lastValueRef.current = JSON.stringify([]);
          onChangeRef.current?.(config.name, []);
        }

        // Fetch raw data for SubModule using dispatch (this will update Redux state)

        dispatch(
          fetchSubModuleRawData({
            lookupName,
            parentValue,
            lookuptype: "Select", // Default to Select for SubModule lookups
          })
        );
        // After this dispatch, the API will be called and rawData will be updated
        // The useEffect will re-run when dependencies change, but won't fetch again due to the guard above

      }
    }

    // REMOVED: The fetchRawLookupData() call was causing duplicate API calls
    // We only use fetchSubModuleRawData dispatch above to avoid infinite loops

    // CRITICAL FIX: Remove lookupsState.rawData and lookupsState.loading from dependencies
    // They cause infinite loops because the effect re-runs when these values change
    // Instead, we check these values inside the effect and use requestedLookupsRef to prevent duplicate calls
    // Also remove value from dependencies - it causes loops when data is updated
    // Only depend on the specific parent field value and config, not entire formData or lookup state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    configAny.LookupName || configAny.lookupName || config.lookupName,
    actualParentField,
    parentFieldValueString, // CRITICAL: Include this to trigger when parent value is set in formData
    config.name,
    // Note: We intentionally exclude:
    // - lookupsState.rawData and lookupsState.loading (cause infinite loops)
    // - value (causes loops when data is updated)
    // The effect reads these values directly from the hook but doesn't depend on them
  ]);

  // Separate useEffect to watch for rawData changes and populate when data arrives
  // This ensures we populate rows when API response arrives, even if parent value hasn't changed
  useEffect(() => {
    const lookupName = configAny.LookupName || configAny.lookupName || config.lookupName;
    const parentField = actualParentField;

    // Only proceed if both lookupName and parentField are configured
    if (!lookupName || !parentField) return;

    // Don't auto-populate if data already exists (from value prop in edit mode)
    const hasExistingData = value && Array.isArray(value) && value.length > 0;
    if (hasExistingData) return;

    // Get parent value using the same utility as Report component
    let parentValue: any = getParentIdFromFormData(formData, parentField);

    // If still not found and parentField is "parentid", also try estimate_id (for jobcard from estimation)
    if (!parentValue && parentField === "parentid") {
      parentValue = formData["estimate_id"] || formData["estimation_id"] || null;
    }

    // Convert to string for consistency
    parentValue = parentValue ? String(parentValue) : parentValue;

    // Only proceed if parent value exists
    if (!parentValue) return;

    const lookupKey = `${lookupName}_${parentValue}`;
    const lookupData = lookupsState.rawData[lookupKey] || [];
    const isLoading = lookupsState.loading[lookupKey] || false;

    // Debug logging for data arrival watcher


    // Only populate if data is available, not loading, and we don't already have data
    const hasExistingDataInState = data && Array.isArray(data) && data.length > 0;
    if (lookupData.length > 0 && !isLoading && !hasExistingDataInState) {
      // Check if this data was already processed
      const lookupDataString = JSON.stringify(lookupData);
      const lastLookupDataString = JSON.stringify(lastLookupDataRef.current);
      if (lastLookupDataString === lookupDataString) {
        return; // Already processed this data
      }



      // Transform lookup data into rows
      const transformedRows = lookupData.map((lookupItem: any) => {
        const row: Record<string, any> = {};

        config.childControls?.forEach((control: any) => {
          const fieldName = control.name || control.BindValue;
          const jsonAttribute = control.Json_Attribute;

          if (fieldName && jsonAttribute) {
            // Try to get value from lookup item using Json_Attribute
            let value =
              lookupItem[jsonAttribute] ||
              lookupItem[jsonAttribute.toLowerCase()] ||
              lookupItem[jsonAttribute.toUpperCase()] ||
              lookupItem[jsonAttribute.replace(/_/g, "")] ||
              "";

            // If no value found, try using fieldName as fallback
            if (!value) {
              value =
                lookupItem[fieldName] ||
                lookupItem[fieldName.toLowerCase()] ||
                lookupItem[fieldName.toUpperCase()] ||
                "";
            }

            // Handle special cases for dropdown fields
            if (
              (control.componentType === ComponentType.SELECT_DROPDOWN ||
                control.componentType === ComponentType.SEARCHABLE_DROPDOWN) &&
              control.lookupName
            ) {
              if (!value || value === "") {
                value = lookupItem.id || lookupItem.value || value;
              }
              value = value ? String(value) : value;
            }

            // Apply default value if no value found
            if (!value && control.Default_Value !== undefined) {
              value = control.Default_Value;
            }

            row[fieldName] = value;
          } else if (fieldName) {
            row[fieldName] =
              lookupItem[fieldName] ||
              lookupItem[fieldName.toLowerCase()] ||
              control.Default_Value ||
              "";
          }
        });

        return row;
      });

      // Evaluate expressions for each row
      const evaluateAllExpressionsForRow = (row: any, rowIndex: number) => {
        const evaluatedRow = { ...row };
        config.childControls?.forEach((control: any) => {
          if (control.ExpressionV2) {
            try {
              // Simple expression evaluation - replace field references with values
              let expression = control.ExpressionV2;
              const fieldName = control.name || control.BindValue;

              // Replace [fieldName] with actual values from the row
              const fieldMatches = expression.match(/\[([^\]]+)\]/g);
              if (fieldMatches) {
                fieldMatches.forEach((match: string) => {
                  const refFieldName = match.replace(/[\[\]]/g, "");
                  const refValue = evaluatedRow[refFieldName] || 0;
                  expression = expression.replace(match, String(refValue));
                });
              }

              // Evaluate the expression
              const result = eval(expression);
              evaluatedRow[fieldName] = result;
            } catch (error) {

            }
          }
        });
        return evaluatedRow;
      };

      const rowsWithExpressions = transformedRows.map((row: any, index: number) => {
        return evaluateAllExpressionsForRow(row, index);
      });

      // Update state
      lastLookupDataRef.current = lookupData;
      lastPopulatedDataRef.current = JSON.stringify(rowsWithExpressions);
      isUpdatingFromInternalRef.current = true;
      setData(rowsWithExpressions);
      onChangeRef.current?.(config.name, rowsWithExpressions);

      // Mark that data was loaded so footer calculation can trigger
      dataLoadedRef.current = true;


    }
  }, [
    // Watch for changes to rawData for this specific lookup
    lookupsState.rawData[`${configAny.LookupName || configAny.lookupName || config.lookupName}_${parentFieldValueString}`],
    lookupsState.loading[`${configAny.LookupName || configAny.lookupName || config.lookupName}_${parentFieldValueString}`],
    configAny.LookupName || configAny.lookupName || config.lookupName,
    actualParentField,
    parentFieldValueString,
    config.name,
    value,
    data,
    formData,
  ]);

  const containerStyles: React.CSSProperties = {
    backgroundColor: config.style?.backgroundColor || "transparent",
    padding: config.style?.padding || 0,
    borderRadius: config.style?.borderRadius || 8,
    marginBottom: config.style?.margin || 5,
    overflowX: "auto",
  };

  const renderField = (control: any, rowIndex: number, fieldName: string) => {
    // Try to get value using fieldName first, then try Json_Attribute as fallback
    const jsonAttribute = control.Json_Attribute;
    const row = data[rowIndex] || {};
    let fieldValue = row[fieldName];

    // If value not found by fieldName and Json_Attribute exists, try using Json_Attribute
    // CRITICAL: This is essential for edit mode where backend returns data with Json_Attribute field names
    if (
      (fieldValue === undefined || fieldValue === null || fieldValue === "") &&
      jsonAttribute
    ) {
      fieldValue = row[jsonAttribute];

      // Also try case-insensitive and underscore variations for robustness
      if ((fieldValue === undefined || fieldValue === null || fieldValue === "") && row) {
        const possibleKeys = [
          jsonAttribute.toLowerCase(),
          jsonAttribute.toUpperCase(),
          jsonAttribute.replace(/_/g, ""),
          fieldName.toLowerCase(),
          fieldName.toUpperCase(),
        ];
        for (const key of possibleKeys) {
          if (row[key] !== undefined && row[key] !== null && row[key] !== "") {
            fieldValue = row[key];

            break;
          }
        }
      }
    }

    // Default to empty string if still no value
    if (fieldValue === undefined || fieldValue === null) {
      fieldValue = "";
    }

    // CRITICAL: For dropdowns, ensure value is properly formatted (string for matching)
    const isDropdown =
      control.componentType === ComponentType.SELECT_DROPDOWN ||
      control.componentType === ComponentType.SEARCHABLE_DROPDOWN ||
      control.componentType === "selectDropdown" ||
      control.componentType === "searchableDropdown";

    if (isDropdown) {
      // Convert to string for consistent matching with option values
      if (fieldValue !== "" && fieldValue !== undefined && fieldValue !== null) {
        fieldValue = String(fieldValue);
      }

      // CRITICAL: Debug logging for dropdown value extraction - always log for dropdowns
      const lookupName = control.LookupName || control.lookupName;

    }
    // Handle both old format (PlaceHolder) and new format (placeholder)
    const placeholder = control.placeholder || control.PlaceHolder || "";
    const inputType = control.inputType || InputType.TEXT;
    // Allow editing when row is in edit mode, unless explicitly disabled
    const isReadOnly =
      (control.IsReadOnly || control.disabled || config.disabled) && editingRowIndex !== rowIndex;

    // Handle field value change
    // SelectDropdown and SearchableDropdown call onChange with (name, value)
    // TextField and other components may call it with just (value)
    const handleFieldChange = (nameOrValue: any, value?: any) => {
      // If two parameters are provided, the first is name and second is value
      // If one parameter is provided, it's the value
      let actualValue = value !== undefined ? value : nameOrValue;

      // CRITICAL FIX: Ensure the field name is correctly identified
      // When SearchableDropdown/SelectDropdown calls onChange(config.name, optionValue),
      // nameOrValue is the field name and value is the selected value
      // But we should use the fieldName from the closure to ensure consistency
      const actualFieldName =
        value !== undefined && typeof nameOrValue === "string"
          ? nameOrValue
          : fieldName;

      // For dropdowns, also store the full selected option (like old framework's SelectedJson.Data)
      // This allows ExpressionV2 to access properties like purchase_rate directly from rowData
      if (
        control.componentType === ComponentType.SEARCHABLE_DROPDOWN ||
        control.componentType === ComponentType.SELECT_DROPDOWN ||
        control.componentType === "searchableDropdown" ||
        control.componentType === "selectDropdown"
      ) {
        const lookupName = control.LookupName || control.lookupName;
        if (lookupName && actualValue) {
          const parentField = control.Parent_Field || control.parentField;
          const actualParentField = parentField || config.parentField;
          const parentValue = actualParentField
            ? formData[actualParentField]
            : undefined;

          let lookupKey = lookupName;
          if (actualParentField && parentValue) {
            lookupKey = `${lookupName}_${parentValue}`;
          }

          // Find the selected option from Redux lookups (for label/value)
          const lookupOptions = lookupsState.lookups[lookupKey] || [];
          let selectedOption = lookupOptions.find(
            (opt: any) =>
              opt.value === actualValue ||
              String(opt.value) === String(actualValue) ||
              opt.id === actualValue ||
              String(opt.id) === String(actualValue)
          );

          // CRITICAL: If not found by value/id, check if actualValue is a label (like "5%")
          // This handles cases where default value is set as label instead of ID
          if (!selectedOption && typeof actualValue === "string") {
            selectedOption = lookupOptions.find(
              (opt: any) =>
                opt.label === actualValue ||
                String(opt.label) === String(actualValue) ||
                (opt as any).name === actualValue ||
                String((opt as any).name) === String(actualValue)
            );

            // If found by label, convert to ID
            if (selectedOption) {
              const idValue = selectedOption.value || (selectedOption as any).id || actualValue;

              if (idValue !== actualValue) {

                // Update actualValue to use the ID
                actualValue = idValue;
              }
            }
          }

          // CRITICAL FIX: Use rawData to get ALL properties (including purchase_rate)
          // The transformed lookup options only have label/value/disabled
          // IMPORTANT: Don't initialize from selectedOption - only set if we find rawItem
          const rawData = lookupsState.rawData[lookupKey] || [];
          let fullSelectedOption: any = null; // Only set if rawItem is found

          if (rawData.length > 0) {
            // Find the matching item in rawData (has all original properties from API)
            const rawItem = rawData.find(
              (item: any) =>
                item.id === actualValue ||
                String(item.id) === String(actualValue) ||
                item.value === actualValue ||
                String(item.value) === String(actualValue) ||
                item.codeid === actualValue ||
                String(item.codeid) === String(actualValue) ||
                (item.product_id &&
                  String(item.product_id) === String(actualValue))
            );

            if (rawItem) {
              // Merge: Use rawItem (has all properties) and override with selectedOption (has correct label/value)
              fullSelectedOption = {
                ...rawItem, // All original properties from API (including purchase_rate, sale_rate, etc.)
                ...(selectedOption || {}), // Override with transformed label/value if available
              };
            }
          }

          // Store the full selected option in row data (like old framework's SelectedJson.Data)
          // Use a special field name: _${fieldName}_selectedOption
          // CRITICAL: Only store if we have fullSelectedOption from rawData (has all properties)
          // Don't store if it only has label/value/disabled (which means rawData wasn't available)
          const selectedOptionKey = `_${actualFieldName}_selectedOption`;
          if (
            fullSelectedOption &&
            Object.keys(fullSelectedOption).length > 3
          ) {
            // Only store if it has more than just label/value/disabled (has properties from rawData)
            // Store the selected option in a temporary location so updateRowField can access it
            // updateRowField will handle storing it in the row data
            (control as any)._tempSelectedOption = fullSelectedOption;
            (control as any)._tempSelectedOptionKey = selectedOptionKey;
          }
          // If fullSelectedOption doesn't have properties, fallback in updateRowField will handle it
        }
      }

      // CRITICAL FIX: Log the update for debugging


      updateRowField(rowIndex, actualFieldName, actualValue, control);
    };

    // Normalize control config for field components
    // Don't render labels in field components since they're already in table headers
    const normalizedControl = {
      ...control,
      name: fieldName,
      label: "", // Empty label to prevent field components from rendering their own labels
      placeholder: placeholder,
      required: control.IsMandatory || control.required || false,
      disabled: isReadOnly,
      defaultValue: control.Default_Value || control.defaultValue || "",
    };

    switch (control.componentType) {
      case ComponentType.SEARCHABLE_DROPDOWN:
      case "searchableDropdown": {
        const lookupName = control.LookupName || control.lookupName;
        const parentField = control.Parent_Field || control.parentField;
        let options = control.options || [];

        if (lookupName) {
          // CRITICAL: Calculate lookupKey the SAME way as in debug logging above
          // Only use parentField from the control itself, NOT from config.parentField
          // Config.parentField is for SubModule-level lookups, not individual control lookups
          let lookupKey = lookupName;
          let actualParentValue: any = undefined;

          if (parentField) {
            actualParentValue = formData[parentField];
            if (actualParentValue !== undefined && actualParentValue !== null && actualParentValue !== "") {
              lookupKey = `${lookupName}_${actualParentValue}`;
            }
          }

          const lookupData = lookupsState.lookups[lookupKey];
          options = Array.isArray(lookupData) ? lookupData : [];

          // CRITICAL: If options are empty but we have a value, check if they exist under a different key
          // This handles cases where options were previously fetched incorrectly (with config.parentField)
          // This is a migration/fallback to handle existing cached data
          if (fieldValue && fieldValue !== "" && options.length === 0) {
            // Try with config.parentField as fallback (for previously incorrectly fetched options)
            const configParentField = config.parentField;
            if (configParentField && !parentField) {
              const configParentValue = formData[configParentField];
              if (configParentValue !== undefined && configParentValue !== null && configParentValue !== "") {
                const fallbackLookupKey = `${lookupName}_${configParentValue}`;
                const fallbackOptions = lookupsState.lookups[fallbackLookupKey] || [];
                if (fallbackOptions.length > 0) {

                  options = fallbackOptions;
                  lookupKey = fallbackLookupKey; // Update for logging
                }
              }
            }
          }

          // Note: Fetching is handled in useEffect, not during render
        }

        // Ensure options is always an array
        const safeOptions = Array.isArray(options) ? options : [];

        // CRITICAL: Normalize fieldValue for dropdown matching
        // Convert to string if it's a number, and ensure it matches option value format
        let normalizedFieldValue = fieldValue;
        if (normalizedFieldValue !== undefined && normalizedFieldValue !== null && normalizedFieldValue !== "") {
          // Convert to string for consistent matching with option values
          normalizedFieldValue = String(normalizedFieldValue);
        } else {
          normalizedFieldValue = undefined;
        }

        // Debug logging for edit mode dropdown values


        // Handle refresh for journal_ledgers and contra_ledgers when dropdown is opened
        // Note: We skip clearing inside SubModule to prevent SubModule data loss
        // The clearing will happen in SearchableDropdown component itself, not here
        const handleDropdownFocus = () => {
          // Refresh ALL independent lookups on the screen when dropdown opens
          // This ensures all dropdowns have the latest data and prevents value mismatches
          if (onRefreshAllIndependentLookups) {
            onRefreshAllIndependentLookups();
          }
        };

        return (
          <SearchableDropdown
            config={{
              ...normalizedControl,
              componentType: ComponentType.SEARCHABLE_DROPDOWN,
              options: safeOptions,
              lookupName: lookupName,
              parentField: parentField,
            }}
            value={normalizedFieldValue}
            onChange={handleFieldChange}
            onFocus={handleDropdownFocus}
          />
        );
      }

      case ComponentType.SELECT_DROPDOWN:
      case "selectDropdown": {
        const lookupName = control.LookupName || control.lookupName;
        const parentField = control.Parent_Field || control.parentField;
        let options = control.options || [];

        if (lookupName) {
          // CRITICAL: Calculate lookupKey the SAME way as in debug logging above
          // Only use parentField from the control itself, NOT from config.parentField
          // Config.parentField is for SubModule-level lookups, not individual control lookups
          let lookupKey = lookupName;
          let actualParentValue: any = undefined;

          if (parentField) {
            actualParentValue = formData[parentField];
            if (actualParentValue !== undefined && actualParentValue !== null && actualParentValue !== "") {
              lookupKey = `${lookupName}_${actualParentValue}`;
            }
          }

          const lookupData = lookupsState.lookups[lookupKey];
          options = Array.isArray(lookupData) ? lookupData : [];

          // CRITICAL: If options are empty but we have a value, check if they exist under a different key
          // This handles cases where options were previously fetched incorrectly (with config.parentField)
          // This is a migration/fallback to handle existing cached data
          if (fieldValue && fieldValue !== "" && options.length === 0) {
            // Try with config.parentField as fallback (for previously incorrectly fetched options)
            const configParentField = config.parentField;
            if (configParentField && !parentField) {
              const configParentValue = formData[configParentField];
              if (configParentValue !== undefined && configParentValue !== null && configParentValue !== "") {
                const fallbackLookupKey = `${lookupName}_${configParentValue}`;
                const fallbackOptions = lookupsState.lookups[fallbackLookupKey] || [];
                if (fallbackOptions.length > 0) {

                  options = fallbackOptions;
                  lookupKey = fallbackLookupKey; // Update for logging
                }
              }
            }
          }


          // Note: Fetching is handled in useEffect, not during render
        }

        // CRITICAL: Normalize fieldValue for dropdown matching
        // Convert to string if it's a number, and ensure it matches option value format
        let normalizedValue: string | number | boolean | undefined = fieldValue;
        if (normalizedValue !== undefined && normalizedValue !== null && normalizedValue !== "") {
          // Convert to string for consistent matching with option values
          normalizedValue = String(normalizedValue);
        } else {
          normalizedValue = undefined;
        }

        // Ensure options is always an array
        const safeOptions = Array.isArray(options) ? options : [];

        // Debug logging for edit mode dropdown values


        // Handle refresh for SelectDropdown in SubModule
        // Note: The SelectDropdown component itself handles refresh for independent lookups
        // This handler is for dependent lookups
        const handleSelectDropdownFocus = () => {
          // Refresh ALL independent lookups on the screen when dropdown opens
          // This ensures all dropdowns have the latest data and prevents value mismatches
          if (onRefreshAllIndependentLookups) {
            onRefreshAllIndependentLookups();
          }
        };

        return (
          <SelectDropdown
            config={{
              ...normalizedControl,
              componentType: ComponentType.SELECT_DROPDOWN,
              options: safeOptions,
              lookupName: lookupName,
              parentField: parentField,
              multiple: control.multiple || false,
            }}
            value={normalizedValue}
            onChange={handleFieldChange}
            onFocus={handleSelectDropdownFocus}
          />
        );
      }

      case ComponentType.DATE_FIELD:
      case "dateField":
        return (
          <DateField
            config={{
              ...normalizedControl,
              componentType: ComponentType.DATE_FIELD,
            }}
            value={fieldValue}
            onChange={handleFieldChange}
          />
        );

      case ComponentType.TEXT_AREA:
      case "textArea":
        return (
          <AutoResizeTextArea
            className="w-full px-3 py-2 border border-gray-400 dark:border-gray-600 rounded-md text-sm outline-none transition-colors duration-200 resize-none overflow-hidden focus:border-primary-500 dark:focus:border-primary-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
            style={{
              minHeight: "100px",
              width: "100%",
              maxWidth: "100%",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              overflowWrap: "break-word"
            }}
            value={fieldValue}
            onChange={(e) => handleFieldChange(e.target.value)}
            placeholder={placeholder}
            disabled={isReadOnly}
            rows={control.rows || 4}
          />
        );

      case ComponentType.TEXT_FIELD:
      case "textField":
      default: {
        // Determine input type
        const htmlInputType =
          inputType === InputType.NUMBER
            ? "number"
            : inputType === InputType.EMAIL
              ? "email"
              : inputType === InputType.PASSWORD
                ? "password"
                : inputType === InputType.PHONE
                  ? "tel"
                  : inputType === InputType.URL
                    ? "url"
                    : "text";

        if (isReadOnly) {
          return (
            <input
              type={htmlInputType}
              className="w-full h-9 px-3 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-100 cursor-not-allowed"
              value={fieldValue}
              readOnly
              placeholder={placeholder}
            />
          );
        }

        // CRITICAL FIX: Ensure TextField in SubModule has proper width when rendered in table cells
        // Override TextField's wrapper to ensure full width in table cells
        return (
          <div className="w-full min-w-0" style={{ minWidth: '80px' }}>
            <div className="w-full px-0">
              <input
                type={htmlInputType}
                className="w-full h-9 px-3 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 dark:focus:border-primary-400 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none transition-colors duration-200"
                value={fieldValue}
                onChange={(e) => handleFieldChange(e.target.value)}
                placeholder={placeholder}
                disabled={isReadOnly}
                style={{ minWidth: '80px' }}
              />
            </div>
          </div>
        );
      }
    }
  };

  // Calculate SUM expression for footer controls
  // Use value prop (formData) instead of local data state to ensure sync with submission data
  const calculateSum = (fieldName: string): number => {
    // Use value prop (from formData) as primary source, fallback to data state
    const dataSource = value && value.length > 0 ? value : data;
    if (!dataSource || dataSource.length === 0) {

      return 0;
    }

    // Find the Json_Attribute for this BindValue in childControls
    // In edit mode, backend returns data with Json_Attribute field names
    // Example: BindValue "txt_amount" → Json_Attribute "amount"
    let jsonAttribute: string | undefined;
    if (config.childControls && Array.isArray(config.childControls)) {
      const control = config.childControls.find((ctrl: any) =>
        (ctrl.BindValue || ctrl.name) === fieldName
      );
      jsonAttribute = control?.Json_Attribute || (control as any)?.json_Attribute;


    }

    let totalSum = 0;
    const rowValues: number[] = [];

    const sumResult = dataSource.reduce((sum, row, index) => {
      // Try BindValue first (for add mode where user enters data)
      let fieldValue = row[fieldName];

      // If not found and Json_Attribute exists, try Json_Attribute (for edit mode backend data)
      // This handles the case where backend returns "amount" but expression uses "txt_amount"
      if ((fieldValue === undefined || fieldValue === null || fieldValue === "") && jsonAttribute) {
        fieldValue = row[jsonAttribute];


      }

      // Also try case-insensitive and underscore variations if still not found
      if ((fieldValue === undefined || fieldValue === null || fieldValue === "") && jsonAttribute) {
        const variations = [
          jsonAttribute.toLowerCase(),
          jsonAttribute.toUpperCase(),
          jsonAttribute.replace(/_/g, ""),
          fieldName.toLowerCase(),
          fieldName.toUpperCase(),
        ];

        for (const variation of variations) {
          if (row[variation] !== undefined && row[variation] !== null && row[variation] !== "") {
            fieldValue = row[variation];

            break;
          }
        }
      }

      // Handle both string and number values
      const numValue =
        fieldValue !== undefined && fieldValue !== null && fieldValue !== ""
          ? parseFloat(String(fieldValue)) || 0
          : 0;

      rowValues.push(numValue);
      return sum + numValue;
    }, 0);

    totalSum = sumResult;



    return totalSum;
  };

  // Evaluate footer control expression (SUM expressions)
  const evaluateFooterExpression = (expression: string): number => {
    if (!expression || typeof expression !== "string") return 0;

    // Check for SUM expression: [SUM:fieldName]
    const sumMatch = expression.match(/\[SUM:(\w+)\]/);
    if (sumMatch) {
      const fieldName = sumMatch[1];
      return calculateSum(fieldName);
    }

    return 0;
  };

  // Track when data is loaded in edit mode to force footer recalculation
  const dataLoadedRef = useRef(false);
  const previousDataLengthRef = useRef(0);
  const previousValueStringRef = useRef<string>("");

  // Detect when edit mode data is loaded
  useEffect(() => {
    const currentDataLength = (value && value.length > 0 ? value : data).length;
    const currentValueString = value ? JSON.stringify(value) : "";
    const hadNoData = previousDataLengthRef.current === 0;
    const hasDataNow = currentDataLength > 0;
    const valueChanged = currentValueString !== previousValueStringRef.current;

    // CRITICAL: Set dataLoadedRef when:
    // 1. Data goes from empty to having data (edit mode load)
    // 2. Value prop changes and has data (edit mode data update)
    if ((hadNoData && hasDataNow) || (valueChanged && hasDataNow)) {
      // Data was just loaded (edit mode)
      dataLoadedRef.current = true;

    }

    previousDataLengthRef.current = currentDataLength;
    previousValueStringRef.current = currentValueString;
  }, [data, value, config.name]);

  // Calculate footer values and update parent form
  // Use both data and value to ensure we calculate from the latest state
  // CRITICAL: This should work for ALL SubModules, regardless of whether they have lookupName/parentField
  useEffect(() => {
    if (!config.FooterControls || !onFormDataChangeRef.current) return;

    const dataSource = value && value.length > 0 ? value : data;

    // CRITICAL: Don't calculate if no data available
    if (!dataSource || dataSource.length === 0) {

      return;
    }

    // CRITICAL: For SubModules with lookupName/parentField, ensure we wait for ExpressionV2 to be evaluated
    // Check if any row has calculated fields (ExpressionV2 results) - if not, wait a bit longer
    const hasCalculatedFields = dataSource.some((row: any) => {
      // Check if row has fields that should be calculated (like amount, vat_amt, net_amt)
      return row.txt_amount !== undefined || row.amount !== undefined ||
        row.txt_vat_amt !== undefined || row.vat_amt !== undefined ||
        row.txt_net_amt !== undefined || row.net_amt !== undefined;
    });

    // Use a longer delay if:
    // 1. Data was just loaded (edit mode)
    // 2. SubModule has lookupName/parentField (might need more time for ExpressionV2)
    // 3. Calculated fields are not yet present
    const hasLookupConfig = !!(config as any).lookupName || !!(config as any).LookupName;
    const needsMoreTime = dataLoadedRef.current || hasLookupConfig || !hasCalculatedFields;
    const delay = needsMoreTime ? 500 : 100; // Increased delay for SubModules with lookupName/parentField



    const timeoutId = setTimeout(() => {


      config.FooterControls!.forEach((footerControl: any) => {
        const expression = footerControl.ExpressionV2;
        const jsonAttribute = footerControl.Json_Attribute;

        if (expression && jsonAttribute) {
          const calculatedValue = evaluateFooterExpression(expression);
          // Format to 2 decimal places
          const formattedValue = parseFloat(calculatedValue.toFixed(2));

          // Always update in edit mode when data is first loaded, otherwise check if changed
          const previousValue = previousFooterValuesRef.current[jsonAttribute];
          const shouldUpdate = dataLoadedRef.current || previousValue !== formattedValue;

          if (shouldUpdate) {
            previousFooterValuesRef.current[jsonAttribute] = formattedValue;
            // Update parent form data using Json_Attribute
            onFormDataChangeRef.current?.(jsonAttribute, formattedValue);

            // Debug logging

          }
        }
      });

      // Reset flag after first calculation
      if (dataLoadedRef.current) {
        dataLoadedRef.current = false;
      }
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [data, value, config.FooterControls, config.name]);

  // Render footer control field
  const renderFooterField = (footerControl: any, index: number) => {
    const expression = footerControl.ExpressionV2;
    const bindValue = footerControl.BindValue;
    const label = footerControl.Label || footerControl.label || "";
    const isReadOnly = footerControl.IsReadOnly !== false;
    const isHidden = footerControl.style?.display === "none";
    const isVerticalView = footerControl.IsVerticalView === true;
    const inputType = footerControl.inputType || InputType.NUMBER;
    const defaultValue =
      footerControl.Default_Value || footerControl.defaultValue || "0";

    // Calculate value from expression
    let fieldValue = defaultValue;
    if (expression) {
      const calculatedValue = evaluateFooterExpression(expression);
      // Format to 2 decimal places, but ensure full number is visible
      fieldValue = calculatedValue.toFixed(2);
      // Remove trailing zeros if not needed for better readability
      fieldValue = parseFloat(fieldValue).toString();
    }

    // If hidden, return empty cell
    if (isHidden) {
      return <td key={index} className="p-3 border-b border-gray-200"></td>;
    }

    // Render visible footer field
    const htmlInputType = inputType === InputType.NUMBER ? "number" : "text";

    if (isVerticalView && label) {
      // Vertical view: label above value
      return (
        <td key={index} className="p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div className="flex flex-col">
            <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 font-medium">
              {label}
            </label>
            <input
              type={htmlInputType}
              className="w-full min-h-[36px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-100 font-semibold cursor-not-allowed"
              value={fieldValue}
              readOnly
              style={{ height: 'auto', minHeight: '36px', overflow: 'visible', textOverflow: 'clip' }}
            />
          </div>
        </td>
      );
    } else {
      // Horizontal view or no label
      return (
        <td key={index} className="p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <input
            type={htmlInputType}
            className="w-full min-h-[36px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-100 font-semibold cursor-not-allowed"
            value={fieldValue}
            readOnly
            placeholder={label}
            style={{ height: 'auto', minHeight: '36px', overflow: 'visible', textOverflow: 'clip' }}
          />
        </td>
      );
    }
  };

  // Count active rows (not soft deleted)
  const activeRowsCount = useMemo(() => {
    return data.filter((r) => r.soft_delete !== 1 && r.soft_delete !== "1").length;
  }, [data]);

  // Filter data to remove soft_deleted rows and apply search term (if enabled)
  const filteredData = useMemo(() => {
    // First, filter out any rows marked for soft deletion
    const activeData = data.filter((row) => row.soft_delete !== 1 && row.soft_delete !== "1");

    // If search is not enabled or no search term, return active data
    if (!config.enableSearch || !searchTerm.trim()) {
      return activeData;
    }

    const searchLower = searchTerm.toLowerCase();
    return activeData.filter((row) => {
      // Search through all fields in the row
      return config.childControls?.some((control: any) => {
        const fieldName = control.name || control.BindValue;
        const jsonAttribute = control.Json_Attribute;
        const fieldValue = row[fieldName] || row[jsonAttribute] || "";
        return String(fieldValue).toLowerCase().includes(searchLower);
      });
    });
  }, [data, searchTerm, config.childControls, config.enableSearch]);

  // Function to start editing a row
  const startEdit = (index: number) => {
    setEditingRowIndex(index);
  };

  // Function to cancel editing
  const cancelEdit = () => {
    setEditingRowIndex(null);
  };

  // Function to view report - opens side drawer (HTML) or PDF modal based on viewAction config
  const handleViewInvoice = async (row: Record<string, any>, index: number) => {
    if (!config.viewAction) return;

    const drawerReportName = config.viewAction.drawerReportName;
    const pdfSettings = config.viewAction.pdfSettings || config.viewAction.reportApi;

    if (!drawerReportName && !pdfSettings) {
      console.error("[SubModule View] No drawerReportName or pdfSettings configured");
      return;
    }

    // Get record ID from row - try multiple field names (supports invoice, purchase, etc.)
    const idField = config.viewAction.idField || "id";
    let recordId =
      row[idField] ||
      row.id ||
      row.invoice_id ||
      row.purchase_id ||
      row.invoice_no ||
      row.purchase_no ||
      row.txt_invoice_no ||
      row.txt_purchase_no ||
      "";

    // If no ID in row, try to get from parent formData (for purchase line items, etc.)
    if (!recordId && formData) {
      recordId =
        formData.id ||
        formData.purchase_id ||
        formData.invoice_id ||
        formData.purchase_no ||
        formData.invoice_no ||
        formData.txt_purchaseno ||
        "";
    }

    if (!recordId) {
      console.error("[SubModule View] No record ID found in row or formData:", { row, formDataKeys: formData ? Object.keys(formData) : [] });
      return;
    }

    // --- Side drawer (HTML report) - same as Jobcard Summary ---
    if (drawerReportName) {
      try {
        dispatch(setHTMLDrawerLoading(true));
        dispatch(openHTMLDrawer({ htmlContent: "", reportName: drawerReportName }));

        const payload = {
          ...row,
          ...formData,
          id: recordId,
          invoice_id: recordId,
          purchase_id: recordId,
          invoice_no: row.invoice_no || row.txt_invoice_no || formData?.invoice_no || recordId,
          purchase_no: row.purchase_no || row.txt_purchase_no || formData?.purchase_no || formData?.txt_purchaseno || recordId,
        };

        const response = await apiService.request<string | {
          success?: boolean;
          data?: string;
          [key: string]: any;
        }>({
          endpoint: "/pabreport/report",
          method: "POST",
          params: { name: drawerReportName },
          payload,
          includeAuthToken: true,
          errorHandling: "throw",
          responseType: "json",
        });

        let htmlContent: string | null = null;
        if (typeof response === "string" && response.includes("<html")) {
          htmlContent = response;
        } else if (response && typeof response === "object") {
          const data = response.data ?? response.content ?? response.html ?? response.body;
          if (typeof data === "string" && data.length > 0) {
            htmlContent = data;
          }
        }
        if (htmlContent) {
          dispatch(openHTMLDrawer({ htmlContent, reportName: drawerReportName }));
        } else {
          throw new Error("No HTML content found in response");
        }
      } catch (error) {
        console.error("[SubModule View] Error opening HTML drawer:", error);
        dispatch(
          setHTMLDrawerError(
            error instanceof Error ? error.message : "Failed to load report"
          )
        );
      }
      return;
    }

    // --- PDF viewer (existing behavior) ---
    if (!pdfSettings) return;

    try {
      dispatch(setPDFViewerLoading(true));

      const encryptedUserData =
        typeof window !== "undefined"
          ? localStorage.getItem("encryptedUserData")
          : null;
      if (!encryptedUserData) {
        throw new Error("User not authenticated");
      }

      const decryptedData = authService.decryptData(encryptedUserData);
      if (!decryptedData) {
        throw new Error("Failed to decrypt user data");
      }

      const username =
        decryptedData.empName ||
        decryptedData.userName ||
        decryptedData.loginName ||
        "";
      const logined_userid =
        decryptedData.empId || decryptedData.userId || decryptedData.id || "1";

      const payload = {
        ...row,
        ...formData,
        id: recordId,
        invoice_id: recordId,
        purchase_id: recordId,
        invoice_no: row.invoice_no || row.txt_invoice_no || formData?.invoice_no || recordId,
        purchase_no: row.purchase_no || row.txt_purchase_no || formData?.purchase_no || formData?.txt_purchaseno || recordId,
        username: username,
        logined_userid: logined_userid,
      };

      const response = await apiService.request<{
        success?: boolean;
        data?: string;
        [key: string]: any;
      }>({
        endpoint: "/pabreport/pdf",
        method: "POST",
        params: { name: pdfSettings },
        payload,
        includeAuthToken: true,
        errorHandling: "throw",
        responseType: "json",
      });

      let pdfBlob: Blob | null = null;
      if (response instanceof Blob) {
        pdfBlob = response;
      } else if (response && typeof response === "object") {
        const base64Data = response.data || response.content || response.pdf;
        if (typeof base64Data === "string" && base64Data.length > 0) {
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          pdfBlob = new Blob([new Uint8Array(byteNumbers)], { type: "application/pdf" });
        }
      }

      if (pdfBlob) {
        const pdfUrl = URL.createObjectURL(pdfBlob);
        // Open in new tab to avoid content policy / CSP errors with embedded viewer
        if (config.viewAction?.openPdfInNewTab) {
          const newWindow = window.open(pdfUrl, "_blank", "noopener,noreferrer");
          if (newWindow) {
            const revokeWhenClosed = () => {
              const interval = setInterval(() => {
                if (newWindow.closed) {
                  clearInterval(interval);
                  URL.revokeObjectURL(pdfUrl);
                }
              }, 500);
            };
            revokeWhenClosed();
          } else {
            URL.revokeObjectURL(pdfUrl);
            dispatch(setPDFViewerError("Popup blocked. Please allow popups for this site to view the PDF."));
          }
        } else {
          dispatch(openPDFViewer({ pdfUrl, reportName: pdfSettings }));
        }
      } else {
        throw new Error("No PDF data found in response");
      }
    } catch (error) {
      console.error("[SubModule View] Error opening PDF:", error);
      dispatch(
        setPDFViewerError(
          error instanceof Error ? error.message : "Failed to load PDF"
        )
      );
      dispatch(setPDFViewerLoading(false));
    }
  };

  // Function to save edited row
  const saveEdit = (index: number) => {
    // Check for duplicates before saving
    const row = data[index];
    const firstTextField = config.childControls?.find(
      (c: any) => c.componentType === ComponentType.TEXT_FIELD || c.componentType === "textField"
    ) as any;

    if (firstTextField && row) {
      const fieldName = firstTextField.name || firstTextField.BindValue;
      const jsonAttribute = firstTextField.Json_Attribute;
      if (fieldName || jsonAttribute) {
        const editedValue = (fieldName && row[fieldName]) || (jsonAttribute && row[jsonAttribute]) || "";

        if (editedValue && String(editedValue).trim() !== "") {
          const isDuplicate = data.some((r, i) => {
            if (i === index) return false; // Skip the current row being edited
            const rowValue = (fieldName && r[fieldName]) || (jsonAttribute && r[jsonAttribute]) || "";
            return String(rowValue).toLowerCase().trim() === String(editedValue).toLowerCase().trim();
          });

          if (isDuplicate) {
            const fieldLabel = firstTextField.Label || firstTextField.label || "This item";
            setError(`${fieldLabel} "${editedValue}" already exists. Please use a different name.`);
            return; // Don't exit edit mode if duplicate found
          }
        }
      }
    }

    // Clear any previous errors
    setError("");
    setEditingRowIndex(null);
    // The row is already updated through updateRowField, so we just need to exit edit mode
  };

  // Determine button position (default: bottom)
  const addButtonPosition = config.addButtonPosition || "bottom";

  // Determine if data is loading for this submodule
  const lookupName = configAny.LookupName || configAny.lookupName || config.lookupName;
  const isLoading = useMemo(() => {
    if (!lookupName || !actualParentField) return false;

    // Check loading state for the current parent value
    const parentValue = parentFieldValueString;
    if (!parentValue) return false;

    const lookupKey = `${lookupName}_${parentValue}`;
    return lookupsState.loading[lookupKey] || false;
  }, [lookupName, actualParentField, parentFieldValueString, lookupsState.loading]);

  return (
    <div
      className={`border overflow-hidden ${(error || externalError) ? "border-2 border-danger-500 dark:border-danger-500" : "border-gray-300 dark:border-gray-600"
        }`}
      style={containerStyles}
      data-field-name={config.name}
    >
      {config.label && (
        <label className="block mb-3 bg-gray-100 dark:bg-gray-700 p-2 text-gray-900 dark:text-gray-100">
          {config.label}
          {config.required && <span className="text-danger-500 dark:text-danger-400 ml-1">*</span>}
        </label>
      )}

      {/* Show "No Data Found" message and "Add New" button only when no data, table not shown, AND not loading */}
      {config.enableAddAction !== false && !showTable && activeRowsCount === 0 && !isLoading && (
        <div className="p-10 flex flex-col items-center justify-center text-center">
          <div className="bg-primary-50 dark:bg-primary-900/30 rounded-full p-3 mb-3">
            <svg
              className="h-8 w-8 text-primary-600 dark:text-primary-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
          </div>
          <h3 className="text-gray-900 dark:text-gray-100 font-medium text-lg">No Data Found</h3>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1 mb-6 max-w-sm">
            No records found here yet. Click the button below to add a new entry.
          </p>
          <button
            className="inline-flex items-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-md transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-70 disabled:cursor-not-allowed"
            onClick={addRow}
            disabled={
              config.disabled ||
              !!(config.maxRows && activeRowsCount >= config.maxRows)
            }
          >
            <svg
              className="h-5 w-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            Add New
          </button>
        </div>
      )}

      {/* Show table structure when showTable is true or data exists OR when loading */}
      {(showTable || activeRowsCount > 0 || isLoading) && (
        <div className="overflow-x-auto overflow-y-visible p-0 mb-4">
          {/* Add New Row button and Search at the top (if configured) */}
          {addButtonPosition === "top" && (
            <div className="flex flex-col gap-3 mb-3">
              {/* Add New Row button at the top */}
              {config.enableAddAction !== false && !config.DisableAddNewRow && (
                <div className="flex flex-row-reverse">
                  <button
                    className="w-[38%] mx-[1%] border-2 border-dotted border-primary-600 dark:border-primary-500 bg-white dark:!bg-gray-800 text-primary-600 dark:text-primary-400
         px-4 py-2 rounded-md text-sm font-medium cursor-pointer
         transition-all duration-200
         hover:bg-primary-50 dark:hover:bg-primary-900/30
         disabled:opacity-70 disabled:cursor-not-allowed"
                    onClick={addRow}
                    disabled={
                      config.disabled ||
                      !!(config.maxRows && activeRowsCount >= config.maxRows)
                    }
                  >
                    + Add New Row
                  </button>
                </div>
              )}

              {/* Search input - only show if search is enabled */}
              {config.enableSearch && data.length > 0 && (
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400 dark:text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-10 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm("")}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none transition-colors"
                      title="Clear search"
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Search input at top when button is at bottom */}
          {addButtonPosition === "bottom" && config.enableSearch && activeRowsCount > 0 && (
            <div className="relative mb-3">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400 dark:text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-10 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none transition-colors"
                  title="Clear search"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          )}

          <table className="w-full min-w-[800px] border-collapse text-sm" style={{ tableLayout: 'auto' }}>
            <thead>
              <tr>
                {config.childControls?.map((control: any, index: number) => {
                  // Handle both old format (Label) and new format (label)
                  const label =
                    control.label ||
                    control.Label ||
                    control.name ||
                    control.BindValue;

                  // Check if column should be hidden
                  const isHidden = control.IsHidden === true;

                  // Add min-width for Code column header
                  const isCodeColumn =
                    (control.Label?.toLowerCase() === "code" ||
                      control.label?.toLowerCase() === "code") &&
                    (control.componentType === ComponentType.SEARCHABLE_DROPDOWN ||
                      control.componentType === "searchableDropdown");

                  // CRITICAL FIX: Add minimum width for Qty and Rate column headers to ensure visibility
                  const isQtyColumn =
                    (control.Label?.toLowerCase() === "qty" ||
                      control.label?.toLowerCase() === "qty" ||
                      control.name?.toLowerCase() === "txt_qty" ||
                      control.BindValue?.toLowerCase() === "txt_qty");
                  const isRateColumn =
                    (control.Label?.toLowerCase() === "rate" ||
                      control.label?.toLowerCase() === "rate" ||
                      control.name?.toLowerCase() === "txt_rate" ||
                      control.BindValue?.toLowerCase() === "txt_rate");

                  let headerStyle: React.CSSProperties = {};
                  if (isCodeColumn) {
                    headerStyle = { minWidth: "210px" };
                  } else if (isQtyColumn || isRateColumn) {
                    // Ensure Qty and Rate column headers have minimum width to match cells
                    headerStyle = { minWidth: "100px", width: "100px" };
                  } else if (
                    control.componentType === ComponentType.TEXT_AREA ||
                    control.componentType === "textArea"
                  ) {
                    // Give Text Area columns more width
                    headerStyle = { minWidth: "300px" };
                  }

                  return (
                    <th
                      key={index}
                      className={`${isHidden ? "hidden" : ""} bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-700 dark:to-primary-800 py-1 px-2 text-left font-semibold text-sm text-white border-b-0 border-primary-500/30`}
                      style={headerStyle}
                    >
                      {label}
                    </th>
                  );
                })}
                {showActionsColumn && (
                  <th className="bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-700 dark:to-primary-800 py-1 px-2 text-left font-semibold text-sm text-white border-b-0 border-primary-500/30">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                // Show loading skeleton rows
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={`loading-${i}`} className="animate-pulse">
                    {config.childControls?.map((control: any, index: number) => (
                      <td key={index} className="p-3 border-b border-gray-200 dark:border-gray-700">
                        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                      </td>
                    ))}
                    {showActionsColumn && (
                      <td className="p-3 border-b border-gray-200 dark:border-gray-700">
                        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-8"></div>
                      </td>
                    )}
                  </tr>
                ))
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan={(config.childControls?.length || 0) + (showActionsColumn ? 1 : 0)} className="p-8 text-center text-gray-500 dark:text-gray-400">
                    {config.enableSearch && searchTerm ? "No results found matching your search." : "No data available."}
                  </td>
                </tr>
              ) : (
                filteredData.map((row, filteredIndex) => {
                  // Find the actual index in the original data array
                  const actualRowIndex = data.findIndex((r) => r === row);
                  const isEditing = editingRowIndex === actualRowIndex;

                  return (
                    <tr key={actualRowIndex} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 dark:bg-gray-800 bg-transparent ${isEditing ? 'ring-2 ring-primary-500' : ''}`}>
                      {config.childControls?.map(
                        (control: any, fieldIndex: number) => {
                          // Handle both old format (BindValue) and new format (name)
                          const fieldName = control.name || control.BindValue;

                          // Check if column should be hidden
                          const isHidden = control.IsHidden === true;

                          // Add min-width for Code column (SEARCHABLE_DROPDOWN with code-related labels)
                          const isCodeColumn =
                            (control.Label?.toLowerCase() === "code" ||
                              control.label?.toLowerCase() === "code") &&
                            (control.componentType === ComponentType.SEARCHABLE_DROPDOWN ||
                              control.componentType === "searchableDropdown");

                          // CRITICAL FIX: Add minimum width for Qty and Rate columns to ensure visibility
                          const isQtyColumn =
                            (control.Label?.toLowerCase() === "qty" ||
                              control.label?.toLowerCase() === "qty" ||
                              fieldName?.toLowerCase() === "txt_qty" ||
                              fieldName?.toLowerCase() === "qty");
                          const isRateColumn =
                            (control.Label?.toLowerCase() === "rate" ||
                              control.label?.toLowerCase() === "rate" ||
                              fieldName?.toLowerCase() === "txt_rate" ||
                              fieldName?.toLowerCase() === "rate");

                          let cellStyle: React.CSSProperties = {};
                          if (isCodeColumn) {
                            cellStyle = { minWidth: "210px" };
                          } else if (isQtyColumn || isRateColumn) {
                            // Ensure Qty and Rate columns have minimum width to be visible
                            cellStyle = { minWidth: "100px", width: "100px" };
                          } else if (
                            control.componentType === ComponentType.TEXT_AREA ||
                            control.componentType === "textArea"
                          ) {
                            // Give Text Area columns more width
                            cellStyle = { minWidth: "300px" };
                          }

                          return (
                            <td
                              key={fieldIndex}
                              className={`${isHidden ? "hidden" : ""} p-1 border-b border-gray-200 dark:border-gray-700 align-middle overflow-visible`}
                              style={cellStyle}
                            >
                              <div className="overflow-visible w-full">
                                {renderField(control, actualRowIndex, fieldName)}
                              </div>
                            </td>
                          );
                        }
                      )}
                      {showActionsColumn && (
                        <td className="p-3 border-b border-gray-200 dark:border-gray-700">
                          <div className="flex items-center gap-2">
                            {isEditing ? (
                              <>
                                <button
                                  className="bg-transparent dark:bg-transparent text-green-600 dark:text-green-400 hover:text-white p-2 rounded-md border-none cursor-pointer transition-all duration-200 hover:bg-green-600 dark:hover:bg-green-600 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
                                  onClick={() => saveEdit(actualRowIndex)}
                                  aria-label="Save changes"
                                  title="Save"
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                                    <polyline points="17 21 17 13 7 13 7 21" />
                                    <polyline points="7 3 7 8 15 8" />
                                  </svg>
                                </button>
                                <button
                                  className="bg-transparent dark:bg-transparent text-gray-600 dark:text-gray-400 hover:text-white p-2 rounded-md border-none cursor-pointer transition-all duration-200 hover:bg-gray-600 dark:hover:bg-gray-600 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
                                  onClick={cancelEdit}
                                  aria-label="Cancel editing"
                                  title="Cancel"
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                  </svg>
                                </button>
                              </>
                            ) : (
                              <>
                                {showEditAction && (
                                  <button
                                    className="bg-transparent dark:bg-transparent text-blue-600 dark:text-blue-400 p-2 rounded-md border-none cursor-pointer transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center hover:!bg-blue-600 dark:hover:!bg-blue-600 hover:!text-white dark:hover:!text-white"
                                    onClick={() => startEdit(actualRowIndex)}
                                    disabled={config.disabled}
                                    aria-label="Edit row"
                                    title="Edit"
                                  >
                                    <svg
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                  </button>
                                )}
                                {showViewAction && (
                                  <button
                                    className="bg-transparent dark:bg-transparent text-green-600 dark:text-green-400 p-2 rounded-md border-none cursor-pointer transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center hover:!bg-green-600 dark:hover:!bg-green-600 hover:!text-white dark:hover:!text-white"
                                    onClick={() => handleViewInvoice(row, actualRowIndex)}
                                    disabled={config.disabled}
                                    aria-label="View report"
                                    title="View report"
                                  >
                                    <svg
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      aria-hidden="true"
                                    >
                                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                      <circle cx="12" cy="12" r="3" />
                                    </svg>
                                  </button>
                                )}
                                {showDeleteAction && (
                                  <button
                                    className="bg-transparent dark:bg-transparent text-gray-700 dark:text-gray-300 p-2 rounded-md border-none cursor-pointer transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center hover:!bg-red-600 dark:hover:!bg-red-600 hover:!text-white dark:hover:!text-white"
                                    onClick={() => removeRow(actualRowIndex)}
                                    disabled={
                                      config.disabled ||
                                      !!(config.minRows && activeRowsCount <= config.minRows)
                                    }
                                    aria-label="Remove row"
                                    title="Delete"
                                  >
                                    <svg
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      aria-hidden="true"
                                    >
                                      <path d="M3 6h18" />
                                      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                      <line x1="10" y1="11" x2="10" y2="17" />
                                      <line x1="14" y1="11" x2="14" y2="17" />
                                    </svg>
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
            {config.FooterControls &&
              config.FooterControls.length > 0 &&
              activeRowsCount > 0 && (
                <tfoot>
                  <tr className="bg-gray-50 dark:bg-gray-800 font-semibold">
                    {config.FooterControls.map(
                      (footerControl: any, index: number) =>
                        renderFooterField(footerControl, index)
                    )}
                    {showActionsColumn && (
                      <td className="p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"></td>
                    )}
                  </tr>
                </tfoot>
              )}
          </table>

          {/* Add New Row button at the bottom (if configured) */}
          {addButtonPosition === "bottom" && config.enableAddAction !== false && !config.DisableAddNewRow && (
            <div className="flex flex-row-reverse mt-2">
              <button
                className="w-[38%] mx-[1%] border-2 border-dotted border-primary-600 dark:border-primary-500 bg-white dark:!bg-gray-800 text-primary-600 dark:text-primary-400
         px-4 py-2 rounded-md text-sm font-medium cursor-pointer
         transition-all duration-200
         hover:bg-primary-50 dark:hover:bg-primary-900/30
         disabled:opacity-70 disabled:cursor-not-allowed"
                onClick={addRow}
                disabled={
                  config.disabled ||
                  !!(config.maxRows && activeRowsCount >= config.maxRows)
                }
              >
                + Add New Row
              </button>
            </div>
          )}
        </div>
      )}

      {(error || externalError) && (
        <div className="px-4 py-2 mt-2 mx-2 mb-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <div className="flex-1">
            <p className="text-sm font-medium text-red-900 dark:text-red-100">
              Validation issues detected
            </p>
            <p className="text-xs text-red-700 dark:text-red-300 mt-0.5">
              Please check the validation popup for details
            </p>
          </div>
        </div>
      )}

      {config.helperText && !error && (
        <div className="text-gray-500 dark:text-gray-400 text-sm mt-2">{config.helperText}</div>
      )}
    </div>
  );
};

